// Insurance Scope — redesigned summary card + editor modal
// Replaces the old InsuranceScopeSection inline-editable layout.
// Spec: memory/features/insurance-scope-redesign.md (Apr 24, 2026)
//
// Two-settlement model (Apr 27, 2026; ACV Credit math corrected May 7, 2026):
//   ACV Settlement         = ACV − Deductible
//   ACV to Bardin          = ACV Settlement              ← ACV Credit no longer subtracted (May 7)
//   First Payment to Bardin (before build) = ACV to Bardin + HO Out-of-Pocket
//   Depreciation Settlement (after build)  = (Dep − DEP Credit) + Supplements + PWI
//   Insurance Pays         = ACV to Bardin + Depreciation Settlement
//
//   PWI sits in the Depreciation Settlement — released by the carrier with depreciation, not on the ACV side.
//   When acv_only is true, the entire Depreciation Settlement (dep, supplements, PWI) is forced to 0.
//
//   HO Out-of-Pocket = (Ded + Upgrades + Retail profit + @cost retail) − credits-applied-to-deductible
//   Credits-applied-to-deductible = ACV credit + Misc credit (DEP credit excluded — Apr 27 fix)
//   ACV credit is ONLY applied here (May 7 fix — was being double-counted on the ACV side too).
//   Credits cap at the deductible amount; overage = not collected (surfaces as warning)
//   Initial Invoice = Insurance Pays + HO OOP + Credits Applied
//   Commissionable = Initial Invoice − at_cost_retail − misc_credit − non_commissionable − punch_list_rm_cost

import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Deal, TeamMember, RetailLineItem } from "../../lib/types";
import { fmtUSD } from "../../lib/analytics";
import { calcScope, type ScopeCalc } from "../../lib/scopeCalc";
import SupplementEntryWarning from "../shared/SupplementEntryWarning";
import { formatPhone } from "../../lib/phoneFormat";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import { heicToJpeg } from "../../lib/heicToJpeg";
import AddOnNotesCard from "./AddOnNotesCard";
import { buildAgreementPayload, agreementIsStale, agreementStaleParts, POST_AGREEMENT_STAGES, tradeMoneyOf, type TradeMoney } from "../../lib/agreementPayload";
import { passToNotifyPlan, useBuildSubmitGate } from "./BuildPaymentPlanModal";
import { notifyChadOnBuildApproval } from "../../lib/notifyChadOnBuildApproval";
import { BUILD_APPROVAL_BOARD, buildApprovalsLink, buildSubmitState, canApproveBuilds } from "../../lib/buildApproval";
import {
  canOverrideScope,
  carrierNumbersPatch,
  dealPicks,
  describeNumberChanges,
  fetchDealForScope,
  scopeCachePatch,
  scopeGateRefreshKey,
  scopeGateRefusal,
  useScopeGate,
  type ScopeGateHook,
} from "../../lib/scopeGate";
import { groupPicks, pickTotals, pickedLinesText, type PickedLine } from "../../lib/scopeLines";
import ScopeCheckCard, { PickedLinesList, ScopeSubmitBlock } from "./ScopeCheckCard";
import ScopeGateApproverPanel from "./ScopeGateApproverPanel";
import ScopeLinePicker from "./ScopeLinePicker";

export { calcScope };
export type { ScopeCalc };
// Re-export for back-compat (these moved to lib/agreementPayload on Jun 24, 2026).
export { buildAgreementPayload, agreementIsStale };

// fetchEdge + SUPABASE_URL removed May 13, 2026 — were only used by the
// retired handleShareWithHo function (now in install-agreement-send edge fn).

// calcScope + ScopeCalc live in ../../lib/scopeCalc — see imports above.

// ---------------------------------------------------------------------------
// Agreement status chip
// ---------------------------------------------------------------------------

function AgreementStatusChip({ status, signed, stage }: { status: string; signed: boolean; stage: string | null | undefined }) {
  // Once the agreement is ready for dispatch, the STAGE says where the build
  // is (Oct 23, 2026): the deal-page submit and build_approve() move the stage
  // without touching agreement_status, so the status alone would keep reading
  // "Ready to submit build" after Chad had approved it on the board.
  const build = buildSubmitState(stage);
  if (status === "released" || status === "ready_for_dispatch") {
    if (build === "approved") {
      return <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-emerald-200 text-emerald-900">Build approved ✓</span>;
    }
    if (build === "submitted") {
      return <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">Build submitted · awaiting approval</span>;
    }
    if (status === "released") {
      return <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-emerald-200 text-emerald-900">Agreement released ✓</span>;
    }
    return <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-800">Ready to submit build</span>;
  }
  if (signed) {
    return <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Signed ✓</span>;
  }
  const map: Record<string, { label: string; cls: string }> = {
    draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
    pending_approval: { label: "Pending Approval", cls: "bg-amber-100 text-amber-800" },
    approved: { label: "Approved", cls: "bg-blue-100 text-blue-700" },
    changes_requested: { label: "Changes Requested", cls: "bg-red-100 text-red-700" },
    sent: { label: "Sent to HO", cls: "bg-indigo-100 text-indigo-700" },
  };
  const s = map[status];
  if (!s) return null;
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
}

// buildAgreementPayload / agreementIsStale / POST_AGREEMENT_STAGES moved to
// lib/agreementPayload.ts (Jun 24, 2026) and imported above, so the homeowner
// SigningPage can read the live payload without importing this staff component.

// ---------------------------------------------------------------------------
// Scope Summary Card — renders on the Overview tab (read-only view + actions)
// ---------------------------------------------------------------------------

/**
 * Sep 15, 2026 (Greg: "hide from rm"): mask ALL three supplement columns before
 * running the calc for a field role. The Apr 30 split added
 * supplement_invoice_amount / supplement_approved_amount but the three read-only
 * surfaces in this file kept zeroing only the legacy `supplements` column, so
 * approved supplement dollars leaked into an RM's Depreciation Settlement and
 * Initial Invoice tiles. Mirrors businessSnapshot.sanitizeDealForField and the
 * editor modal's own masking.
 */
function maskSupplementsForField<T extends Partial<Deal>>(deal: T): T {
  return { ...deal, supplements: 0, supplement_invoice_amount: 0, supplement_approved_amount: 0 };
}

// ---------------------------------------------------------------------------
// Trades billed to the homeowner (Oct 29, 2026, per Greg: "the trade sheet is
// the first step; the homeowner agreement picks it up from there")
// ---------------------------------------------------------------------------

// ⚠ Mirror of trade_can_see_prices() / build-calendar PRICE_HIDDEN_EMAILS:
// Jeremy is a super user but kept off trade money. The floor (cost / 0.7) or
// the upside next to the customer price would hand him the sub's cost, so he
// sees the customer amounts only — the same as on the trade sheet.
const TRADE_PRICES_HIDDEN_EMAILS = new Set(["jcohan@bardinroofing.com"]);
function seesTradeSplit(member: TeamMember | null): boolean {
  return !TRADE_PRICES_HIDDEN_EMAILS.has((member?.email ?? "").trim().toLowerCase());
}

/**
 * The trades billed to the homeowner, read-only: they come from the trade
 * sheet (deals.trade_ho_* kept by SQL trade_sync_deal) and are never typed
 * here. One customer line per trade, due at completion. Staff also see the
 * 30% floor (at cost to the commission formula) and the upside (the pool paid
 * as commission). On a signed agreement a trade added later is signed by the
 * homeowner on its own add-on from the trade sheet, so the card says so.
 */
function TradesBilledRows({
  trades,
  showSplit,
  signed = false,
  bordered = true,
}: {
  trades: TradeMoney;
  showSplit: boolean;
  signed?: boolean;
  bordered?: boolean;
}) {
  if (trades.total <= 0) return null;
  return (
    <div className={`py-1.5 ${bordered ? "border-t border-slate-100" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-slate-600 min-w-0">
          Trades billed to homeowner{" "}
          <span className="text-[11px] text-slate-400">· from the trade sheet · due at completion</span>
        </div>
        <div className="text-sm font-semibold text-bardin-ink shrink-0">{fmtUSD(trades.total)}</div>
      </div>
      {trades.lines.map((t, i) => (
        <div key={i} className="flex items-center justify-between gap-2 pl-3 text-xs text-slate-500">
          <span className="truncate">{t.label}</span>
          <span className="shrink-0">{fmtUSD(t.amount)}</span>
        </div>
      ))}
      {showSplit && (
        <div className="pl-3 mt-0.5 text-[11px] text-slate-500">
          floor {fmtUSD(trades.floor)} at cost · upside {fmtUSD(trades.upside)} pool
        </div>
      )}
      {signed && (
        <div className="mt-1.5 p-2 rounded bg-slate-50 border border-slate-200 text-[11px] text-slate-600">
          Trades after signing are signed by the homeowner as an add-on (link from the trade sheet)
        </div>
      )}
    </div>
  );
}

/** D10: @ cost retail and trades from the trade sheet both billed — the same
 *  gutter work typed in both places would bill the homeowner twice. */
function AtCostAndTradesNote({ atCost, tradesTotal, inEditor }: { atCost: number; tradesTotal: number; inEditor: boolean }) {
  if (!(atCost > 0 && tradesTotal > 0)) return null;
  return (
    <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
      <span>⚠</span>
      <span>
        @ cost retail ({fmtUSD(atCost)}) and trades from the trade sheet ({fmtUSD(tradesTotal)}) are both billed to the
        homeowner. If the @ cost amount is the same gutter work, clear it {inEditor ? "above" : "in Edit Scope"} so it isn't
        billed twice.
      </span>
    </div>
  );
}

/** D3 (Oct 29, 2026): one bucket per deal — the non-collect revenue (an
 *  insurance deal whose deduction is active) + the trade shortfalls Chad
 *  deducted; the first $500 is forgiven, the rest comes off the commissions.
 *  Display mirror of the Commission board's getNonCollectBucket. */
function nonCollectBucket(nonCollect: number, ncCounts: boolean, tradeLoss: number) {
  const bucket = Math.round(((ncCounts ? nonCollect : 0) + tradeLoss) * 100) / 100;
  return { bucket, over: Math.max(0, Math.round((bucket - 500) * 100) / 100) };
}

export function ScopeSummaryCard({
  deal,
  canEdit,
  role,
  isSuperUser,
  member,
  onEdit,
  onReload,
  hideActions = false,
}: {
  deal: Deal;
  canEdit: boolean;
  role: string | undefined;
  isSuperUser: boolean;
  member: TeamMember | null;
  onEdit: () => void;
  onReload: () => void | Promise<void>;
  /** When true, omit the action buttons / preferred-date form / banners /
   *  modal mount. Used by DealDetail to render the BuildApprovalPrep panel
   *  at the top of the page instead — Apr 30, 2026. */
  hideActions?: boolean;
}) {
  // Supplements (and the totals that include them) are hidden from RM/TL/ASM/ops.
  // Visible only to admin / sales_director / finance / super users — same role gate
  // as the Financials section in DealDetail.
  const canSeeSupplements = isSuperUser || role === "admin" || role === "sales_director" || role === "finance";
  const calc = calcScope(canSeeSupplements ? deal : maskSupplementsForField(deal));
  const hasScope = calc.isRetail ? calc.retailContractTotal > 0 : calc.rcv > 0;
  const status = (deal.agreement_status as string) ?? "none";
  const signed = deal.install_agreement_signed === true;
  const unperfTotal = calc.acvCredit + calc.depCredit;
  // Oct 30, 2026: the picked carrier lines, by kind of work, instead of line numbers.
  const summaryPickGroups = calc.isRetail ? [] : groupPicks(dealPicks(deal));
  const trades = tradeMoneyOf(calc);
  const showTradeSplit = seesTradeSplit(member);

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-start justify-between mb-3 gap-2">
        <div>
          <h3 className="text-sm font-semibold text-bardin-ink">{calc.isRetail ? "Retail Scope" : "Insurance Scope"}</h3>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <AgreementStatusChip status={status} signed={signed} stage={deal.stage} />
            {calc.isRetail && (
              <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-teal-100 text-teal-800">🏷 Retail · 50/50</span>
            )}
            {!calc.isRetail && calc.acvOnly && (
              <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">ACV only</span>
            )}
          </div>
        </div>
      </div>

      {!hasScope ? (
        <div className="text-sm text-slate-500 py-3">
          {calc.isRetail ? "No retail scope entered yet — enter the contract total and squares." : "No scope entered yet."}
        </div>
      ) : calc.isRetail ? (
        <>
          {/* Retail stat tiles */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <StatTile label="50% Deposit (Before Build)" value={calc.retailDeposit} tone="insurance" />
            <StatTile label={trades.total > 0 ? "Due on Completion (50% + trades)" : "50% Due on Completion"} value={calc.retailCompletionPayment} tone="ho" />
            <StatTile label="Contract Total" value={calc.retailContractTotal} />
            <StatTile label={`Base (${calc.retailSquares} sq × ${fmtUSD(calc.retailBasePerSquare)})`} value={calc.retailBaseTotal} />
          </div>
          <TradesBilledRows trades={trades} showSplit={showTradeSplit} signed={signed} />
          <div className="flex items-center justify-between py-1.5 border-t border-slate-100">
            <div className="text-sm text-slate-600">Commission Pool (commissionable)</div>
            <div className="text-sm font-semibold text-emerald-700">{fmtUSD(calc.commissionable)}</div>
          </div>
          {trades.marginLoss > 0 && (() => {
            // Retail: the bucket is the trade shortfalls alone (D3), all off the RM.
            const b = nonCollectBucket(0, false, trades.marginLoss);
            return (
              <div className="mt-2 flex items-start gap-2 p-2 rounded text-xs bg-amber-50 border border-amber-200 text-amber-800">
                <span>📋</span>
                <span>
                  Trade shortfalls {fmtUSD(trades.marginLoss)} · first $500 forgiven ·{" "}
                  {b.over > 0 ? <><span className="font-semibold">{fmtUSD(b.over)}</span> comes off the RM's commission</> : "nothing deducted"}
                </span>
              </div>
            );
          })()}
          {Array.isArray((deal as { retail_items?: RetailLineItem[] | null }).retail_items) &&
            ((deal as { retail_items?: RetailLineItem[] | null }).retail_items?.length ?? 0) > 0 && (
            <div className="mt-2 rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
              {((deal as { retail_items?: RetailLineItem[] | null }).retail_items ?? []).map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate text-slate-700">{it.kind === "roof" ? "🏠 " : ""}{it.label}</span>
                    {it.kind === "at_cost" && (
                      <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">at cost</span>
                    )}
                  </span>
                  <span className="shrink-0 font-medium text-slate-700">{fmtUSD(Number(it.amount) || 0)}</span>
                </div>
              ))}
            </div>
          )}
          {(deal as { retail_scope_description?: string | null }).retail_scope_description && (
            <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
              <span className="font-semibold">Scope: </span>
              {(deal as { retail_scope_description?: string | null }).retail_scope_description}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Stat tiles */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <StatTile label="First Payment (Pre-Build)" value={calc.firstPaymentToBardin} tone="insurance" />
            <StatTile label={calc.acvOnly ? "Dep Settlement (ACV only)" : "Depreciation Settlement"} value={calc.depSettlement} tone="ho" />
            <StatTile label="ACV Settlement" value={calc.acvSettlement} />
            <StatTile label="HO Out-of-Pocket" value={calc.hoOutOfPocket} />
          </div>

          {/* Summary rows */}
          <div className="flex items-center justify-between py-1.5 border-t border-slate-100">
            <div className="text-sm text-slate-600">Initial Invoice</div>
            <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(calc.initialInvoice)}</div>
          </div>
          <TradesBilledRows trades={trades} showSplit={showTradeSplit} signed={signed} bordered={false} />
          <div className="flex items-center justify-between py-1.5">
            <div className="text-sm text-slate-600">Commissionable Revenue</div>
            <div className="text-sm font-semibold text-emerald-700">{fmtUSD(calc.commissionable)}</div>
          </div>
          <AtCostAndTradesNote atCost={calc.atCostRetail} tradesTotal={trades.total} inEditor={false} />

          {/* Warnings */}
          {unperfTotal > 0 && (
            <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              <span>⚠</span>
              <span>
                Un-performed items: ACV {fmtUSD(calc.acvCredit)} credited toward HO deductible; DEP {fmtUSD(calc.depCredit)} not collected from insurance
                {summaryPickGroups.length > 0
                  ? ` (${summaryPickGroups.map((g) => `${g.title} ${fmtUSD(g.acv)}`).join(" · ")})`
                  : deal.items_not_performing_lines ? ` (${deal.items_not_performing_lines})` : ""}
              </span>
            </div>
          )}
          {!calc.isRetail && <AddOnNotesCard deal={deal} className="mt-2" />}
          {calc.creditOverage > 0 && (
            <div className="mt-2 flex items-start gap-2 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800">
              <span>⚠</span>
              <span>Credit overage: {fmtUSD(calc.creditOverage)} not collected</span>
            </div>
          )}
          {calc.nonCollectRevenue > 0 && trades.marginLoss <= 0 && (() => {
            // Gate is the dedicated deduction-active flag (Jun 17, 2026). When
            // inactive (already-paid grandfathered deals), no deduction applies.
            const signed = deal.install_agreement_signed === true;
            const deductActive = (deal as { non_collect_deduct_active?: boolean | null }).non_collect_deduct_active !== false;
            const grandfathered = !deductActive;
            return (
              <div className={`mt-2 flex items-start gap-2 p-2 rounded text-xs ${grandfathered ? "bg-slate-50 border border-slate-200 text-slate-700" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
                <span>📋</span>
                <span>
                  Non-Collect Revenue: <span className="font-semibold">{fmtUSD(calc.nonCollectRevenue)}</span>
                  {grandfathered ? (
                    <> · <span className="italic">grandfathered — no commission deduction applies</span></>
                  ) : calc.nonCollectOverage > 0 ? (
                    <> · overage <span className="font-semibold">{fmtUSD(calc.nonCollectOverage)}</span> {signed ? "deducted" : "will be deducted"} from RM / TL / ASM commission (split recorded at approval)</>
                  ) : null}
                </span>
              </div>
            );
          })()}
          {trades.marginLoss > 0 && (() => {
            // Oct 29, 2026 (D3): the trade shortfalls Chad deducted blend into
            // the non-collect bucket; the first $500 of the bucket is forgiven.
            // A grandfathered deal's non-collect stays out of it.
            const deductActive = (deal as { non_collect_deduct_active?: boolean | null }).non_collect_deduct_active !== false;
            const b = nonCollectBucket(calc.nonCollectRevenue, deductActive, trades.marginLoss);
            const parts = [
              deductActive && calc.nonCollectRevenue > 0 ? `Non-collect ${fmtUSD(calc.nonCollectRevenue)}` : null,
              `trade shortfalls ${fmtUSD(trades.marginLoss)}`,
            ].filter(Boolean) as string[];
            return (
              <div className="mt-2 flex items-start gap-2 p-2 rounded text-xs bg-amber-50 border border-amber-200 text-amber-800">
                <span>📋</span>
                <span>
                  {parts.length > 1 ? `${parts.join(" + ")} = ${fmtUSD(b.bucket)}` : `Trade shortfalls ${fmtUSD(trades.marginLoss)}`}
                  {" · "}first $500 forgiven ·{" "}
                  {b.over > 0
                    ? <><span className="font-semibold">{fmtUSD(b.over)}</span> {signed ? "deducted" : "will be deducted"} from RM / TL / ASM commission</>
                    : "nothing deducted"}
                  {!deductActive && calc.nonCollectRevenue > 0 && (
                    <> · <span className="italic">non-collect {fmtUSD(calc.nonCollectRevenue)} grandfathered — not counted</span></>
                  )}
                </span>
              </div>
            );
          })()}
        </>
      )}

      {/* Action buttons + preferred-date form + lifecycle banners + modal —
          all extracted into ScopeActions Apr 30, 2026 so they can be rendered
          at the top of DealDetail (BuildApprovalPrep panel) on install_meeting
          deals. Falls back to rendering inside the card on other stages. */}
      {!hideActions && (
        <ScopeActions
          deal={deal}
          canEdit={canEdit}
          role={role}
          isSuperUser={isSuperUser}
          member={member}
          onEdit={onEdit}
          onReload={onReload}
          variant="card"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScopeActions — extracted Apr 30, 2026. The agreement lifecycle buttons,
// post-sign preferred-date inline form, and in-flight banners all live here
// so DealDetail can render them prominently at the top of the page on
// install_meeting deals (BuildApprovalPrep panel).
//
// Each instance manages its own modal/share/preferred-date state. Only one
// instance is mounted at a time (DealDetail decides via stage), so there's
// no state-coordination concern.
// ---------------------------------------------------------------------------

export function ScopeActions({
  deal,
  canEdit,
  role,
  isSuperUser,
  member,
  onEdit,
  onReload,
  variant = "card",
  hideScopeEdit = false,
}: {
  deal: Deal;
  canEdit: boolean;
  role: string | undefined;
  isSuperUser: boolean;
  member: TeamMember | null;
  onEdit: () => void;
  onReload: () => void | Promise<void>;
  /** "card" = inside ScopeSummaryCard (mt-3 spacing).
   *  "panel" = standalone in DealDetail BuildApprovalPrep (no extra top margin). */
  variant?: "card" | "panel";
  /** When true, suppress the Enter/Edit Scope button. BuildApprovalPrep
   *  surfaces its own prominent version, so we don't want a duplicate here. */
  hideScopeEdit?: boolean;
}) {
  const canSeeSupplements =
    isSuperUser || role === "admin" || role === "sales_director" || role === "finance";
  const calc = calcScope(canSeeSupplements ? deal : maskSupplementsForField(deal));
  const hasScope = calc.isRetail ? calc.retailContractTotal > 0 : calc.rcv > 0;
  const status = (deal.agreement_status as string) ?? "none";
  const signed = deal.install_agreement_signed === true;

  // Post-sign financial override (May 20, 2026, per Greg). Once an agreement is
  // signed the scope normally locks for everyone. Super-users + the finance/exec
  // tier can still edit financials after signing (corrections, final-invoice
  // reconciliation, supplement true-ups). RM/TL/ASM stay locked post-sign.
  // NOTE: kept in lockstep with the deals_admin_full RLS policy (admin /
  // sales_director / finance + super-user override). Don't add roles here that
  // can't UPDATE deals, or the unlocked button would fail at save. Jennifer
  // (director_of_operations) is covered via the super-user override.
  const canManageFinancials =
    isSuperUser ||
    role === "admin" ||
    role === "sales_director" ||
    role === "finance";
  const canEditScope = canEdit && (!signed || canManageFinancials);
  const canGenerate = canEdit && hasScope && (status === "none" || status === "changes_requested");
  // canShareWithHo / handleShareWithHo retired May 13, 2026 — superseded by
  // <SendAgreementHero /> at top of DealDetail. The new flow supports email +
  // text + both, uses RM email as From, and tracks delivery + opens + signs.
  const showAgreementLink = status !== "none" || signed;

  const colorsChosen = !!deal.shingle_color && !!deal.drip_edge_color;
  const canEnterPreferredDate =
    canEdit && signed && colorsChosen && !deal.ho_preferred_build_date && status !== "released";
  // Stage-aware (Oct 23, 2026): the Submit build button and the "waiting on
  // Chad" banner show only while the build has NOT been submitted. Once the
  // stage is build_submitted the board owns it; once it is schedule_build or
  // later Chad or Robert approved it, whatever agreement_status still says.
  const buildState = buildSubmitState(deal.stage);
  const dispatchReady = status === "ready_for_dispatch" || status === "released";
  const awaitingChadRelease = status === "ready_for_dispatch" && buildState === "before";
  const buildOnBoard = dispatchReady && buildState === "submitted";
  const buildApproved = dispatchReady && buildState === "approved";

  // Setters retired with handleShareWithHo (May 13, 2026). The variables are
  // still read by JSX below (Edit-Scope disabled state + success banner) but
  // never flip true now that the legacy path is gone. Kept as `false`
  // constants via single-element destructure to satisfy noUnusedLocals.
  const [sharing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [shareSuccess] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewModalMode, setReviewModalMode] = useState<"review" | "previewBeforeGenerate">("review");
  const [savingPreferredDate, setSavingPreferredDate] = useState(false);
  const [preferredDateInput, setPreferredDateInput] = useState<string>("");

  const isChadTier = isSuperUser || role === "admin" || role === "sales_director" || role === "finance";
  const isRmTier =
    role === "asm" ||
    role === "team_lead" ||
    role === "roof_team_manager" ||
    role === "restore_manager" ||
    isChadTier;
  const canResumeLegacyDraft = isRmTier && status === "draft";
  const canReview =
    isChadTier && (status === "pending_approval" || (status === "ready_for_dispatch" && buildState === "before"));

  // Oct 30, 2026 — the scope check (migration 20261030_scope_gate): the deal's
  // numbers against the carrier's scope the CRM read. It shows above the buttons
  // on an unsigned insurance deal before the build; while the switch
  // (finance_settings.scope_gate_mode) is "on" the same check stops Submit for
  // approval and Resubmit (trigger deals_scope_gate) unless Alex or Chad override.
  const showScopeCheck =
    isRmTier && !calc.isRetail && !signed && !POST_AGREEMENT_STAGES.has((deal.stage as string) ?? "");
  const scope = useScopeGate(showScopeCheck ? deal.id : null, scopeGateRefreshKey(deal));

  // Jun 11, 2026 — stale-agreement detection (per Greg). The signing page
  // renders from the agreement_data snapshot, NOT live scope columns, so a
  // scope edit after generation leaves the homeowner link showing old numbers
  // (bit the Larry Feldman deal when insurance purchased gutter guards and the
  // adjuster revised). When the snapshot drifts from the live scope, surface a
  // Resubmit button that refreshes the snapshot and sends it back to Chad.
  // Post-sign edits (finance true-ups) are excluded — the signed PDF is the
  // contract; changing the snapshot under it would be a lie.
  //
  // Stage gate (verification-pass fix, same day): deals in the supps lifecycle
  // / post-build stages get routine supplement + non-collect + misc edits from
  // Shannon — expected financial updates, NOT agreement revisions (4 of 6
  // live stale-flagged deals were exactly this: legacy unsigned deals at
  // collecting / supps_*). Banner only renders pre-build, where the signing
  // link is still the live ask.
  //
  // Oct 29, 2026: a trade billed to the homeowner, saved (or repriced, or called
  // off) on the trade sheet after the agreement was generated, makes it stale
  // the same way (agreementPayload's trade keys) — Resubmit and Chad
  // re-approves. A SIGNED agreement never goes stale here: a trade after
  // signing is signed by the homeowner on its own add-on (the trade sheet).
  const [resubmitting, setResubmitting] = useState(false);
  const staleParts =
    isRmTier &&
    !signed &&
    hasScope &&
    ["pending_approval", "approved", "ready_for_dispatch"].includes(status) &&
    !POST_AGREEMENT_STAGES.has((deal.stage as string) ?? "")
      ? agreementStaleParts(deal)
      : null;
  const agreementStale = !!staleParts && (staleParts.scope || staleParts.trades);
  const onlyTradesStale = !!staleParts && staleParts.trades && !staleParts.scope;
  // A resubmit from approved / ready-for-dispatch is a new submission, so the
  // scope check gates it; from pending_approval it isn't (Alex or Chad still
  // meet the check when they approve).
  const resubmitBlocked =
    agreementStale && status !== "pending_approval" && !!scope.status?.applies && !!scope.status?.blocks;

  async function handleResubmitAfterScopeEdit(overrideRequest?: string) {
    setResubmitting(true);
    setActionError(null);
    try {
      const payload = buildAgreementPayload(deal);
      const { error } = await supabase
        .from("deals")
        .update({
          agreement_data: payload,
          agreement_status: "pending_approval",
          agreement_generated_date: new Date().toISOString(),
          agreement_revision_count: (deal.agreement_revision_count ?? 0) + 1,
          // The submission carries the RM's override request (trigger deals_scope_gate stamps who and when).
          ...(overrideRequest ? { scope_gate: { request: { note: overrideRequest } } } : {}),
        })
        .eq("id", deal.id);
      if (error) {
        const refusal = scopeGateRefusal(error);
        if (refusal) {
          setActionError(refusal);
          void scope.refresh();
          return;
        }
        throw error;
      }
      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "agreement",
        content: (onlyTradesStale
          ? "A trade billed to the homeowner changed after the agreement was generated — agreement regenerated with the trade and resubmitted for Chad's approval."
          : "Scope revised after the agreement was generated — agreement regenerated and resubmitted for Chad's approval.")
          + (overrideRequest ? ` Asked Alex or Chad to override the scope check: ${overrideRequest}` : ""),
        metadata: {
          action: "resubmit_after_scope_edit", prior_agreement_status: status, trades_changed: !!staleParts?.trades,
          ...(overrideRequest ? { scope_override_requested: true } : {}),
        },
      });
      // Same Chad ping as the generate-and-submit path.
      const { notifyChadOnAgreementSubmitted } = await import("../../lib/notifyChadOnAgreementSubmitted");
      void notifyChadOnAgreementSubmitted(deal, { overrideRequest: overrideRequest ?? null });
      await onReload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setResubmitting(false);
    }
  }

  function openGeneratePreview() {
    setReviewModalMode("previewBeforeGenerate");
    setReviewModalOpen(true);
  }
  function openReviewModal() {
    setReviewModalMode("review");
    setReviewModalOpen(true);
  }

  // ── Convert to Retail / Revert (Jun 11, 2026, per Greg) ──────────────────
  // For deals where the claim was denied or only partially approved and the
  // homeowner wants to go full retail. Sets is_retail, snapshots the $/sq base
  // from finance_settings, voids any in-flight (unsigned) agreement, and pulls
  // terminal-stage deals (claims_denied etc.) back to install_meeting so the
  // agreement flow can run. Blocked once the agreement is signed — that's a
  // contract; converting after signing is an admin-level correction.
  const [converting, setConverting] = useState(false);
  const TERMINAL_STAGES_FOR_RETAIL = ["claims_denied", "insufficient_damage", "no_thanks", "closed_inspection", "closed_lost", "cancelled"];
  const canConvertToRetail = canEdit && !signed && !calc.isRetail;
  const canRevertToInsurance = canEdit && !signed && calc.isRetail;

  async function handleConvertRetail(toRetail: boolean) {
    const stageIsTerminal = TERMINAL_STAGES_FOR_RETAIL.includes(deal.stage ?? "");
    const msg = toRetail
      ? `Convert this deal to a FULL RETAIL job?\n\n• Payment schedule becomes 50% deposit / 50% on completion\n• Commission pool = contract total minus the $460/sq base (split set per job by Chad)\n• Any unsigned agreement is voided — you'll enter the retail scope and regenerate${stageIsTerminal ? `\n• Stage moves from ${deal.stage} back to install_meeting` : ""}`
      : "Revert this deal back to the INSURANCE scope? The retail numbers stay saved but the insurance schedule takes over again. Any unsigned agreement is voided.";
    if (!window.confirm(msg)) return;
    setConverting(true);
    setActionError(null);
    try {
      const updates: Record<string, unknown> = {
        is_retail: toRetail,
        agreement_status: "none",
      };
      if (toRetail) {
        // Snapshot the base $/sq so later setting changes don't move this deal.
        const { data: setting } = await supabase
          .from("finance_settings")
          .select("value_jsonb")
          .eq("key", "retail_base_per_square")
          .maybeSingle();
        const base = Number(setting?.value_jsonb ?? 460);
        updates.retail_base_per_square = Number.isFinite(base) && base > 0 ? base : 460;
        updates.retail_converted_at = new Date().toISOString();
        if (stageIsTerminal) {
          updates.stage = "install_meeting";
          updates.days_in_stage = 0;
        }
      }
      const { error } = await supabase.from("deals").update(updates).eq("id", deal.id);
      if (error) throw error;
      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "agreement",
        content: toRetail
          ? `Deal converted to FULL RETAIL by ${member?.full_name ?? "staff"} (insurance denied / partial). 50/50 payment schedule, $${updates.retail_base_per_square}/sq base.${stageIsTerminal ? ` Stage moved ${deal.stage} → install_meeting.` : ""}`
          : `Deal reverted to INSURANCE scope by ${member?.full_name ?? "staff"}.`,
        metadata: { action: toRetail ? "convert_to_retail" : "revert_to_insurance", prior_stage: deal.stage },
      });
      await onReload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setConverting(false);
    }
  }

  async function handleSavePreferredDate() {
    if (!preferredDateInput) {
      setActionError("Pick a preferred build date first.");
      return;
    }
    setSavingPreferredDate(true);
    setActionError(null);
    try {
      const { error } = await supabase
        .from("deals")
        .update({
          ho_preferred_build_date: preferredDateInput,
          agreement_status: "ready_for_dispatch",
        })
        .eq("id", deal.id);
      if (error) throw error;

      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "agreement",
        content: `Preferred build date set to ${preferredDateInput}. Sent to Chad to submit the build.`,
        metadata: { action: "preferred_date_set", ho_preferred_build_date: preferredDateInput },
      });

      const { notifyChadOnReadyForDispatch } = await import("../../lib/notifyChadOnReadyForDispatch");
      void notifyChadOnReadyForDispatch({
        ...deal,
        ho_preferred_build_date: preferredDateInput,
      });

      setPreferredDateInput("");
      await onReload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setSavingPreferredDate(false);
    }
  }

  // handleShareWithHo() function removed May 13, 2026 — superseded by the
  // install-agreement-send edge function which is invoked through
  // <SendAgreementHero /> mounted at the top of DealDetail. The new path:
  //   - sends via email AND/OR text (HO picks via the modal chips)
  //   - From: deal owner RM (CC split RM), Reply-To: RM
  //   - writes a deal_agreement_deliveries row + auto-tracks open/sign
  //   - notifies RM via the in-portal notification board
  // Legacy callers should use the edge function or the SendAgreementButton component.

  const topMargin = variant === "card" ? "mt-3" : "";

  return (
    <>
      {/* Scope check (Oct 30, 2026) — above the agreement buttons it gates. */}
      {showScopeCheck && (
        <div className={topMargin}>
          {/* Keyed by the deal: the deal page reuses this card when it opens another deal, and the card's Undo,
              open picker and messages belong to the deal they were made on. */}
          <ScopeCheckCard key={deal.id} deal={deal} scope={scope} member={member} canEdit={canEdit} onReload={onReload} />
        </div>
      )}

      {/* Action buttons — state-driven */}
      <div className={`flex flex-wrap gap-2 ${showScopeCheck ? "" : topMargin}`}>
        {canEditScope && !hideScopeEdit && (
          <button
            onClick={onEdit}
            disabled={sharing || savingPreferredDate}
            className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50"
          >
            {hasScope ? "Edit Scope" : "Enter Scope"}
          </button>
        )}
        {canGenerate && (
          <button
            onClick={openGeneratePreview}
            className="px-3 py-1.5 text-xs font-medium rounded bg-slate-900 text-white hover:bg-slate-800"
          >
            {status === "changes_requested" ? "Regenerate Agreement" : "Generate Install Agreement"}
          </button>
        )}
        {canResumeLegacyDraft && (
          <button
            onClick={openReviewModal}
            className="px-3 py-1.5 text-xs font-medium rounded bg-slate-900 text-white hover:bg-slate-800"
          >
            Submit for Approval
          </button>
        )}
        {canReview && (
          <button
            onClick={openReviewModal}
            className="px-3 py-1.5 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700"
          >
            {status === "ready_for_dispatch" ? "Submit build" : "Review & Approve"}
          </button>
        )}
        {/* "Share with Homeowner" button removed May 13, 2026 — superseded by
            <SendAgreementHero /> at top of DealDetail (broader: email + text + both,
            delivery history, RM-as-sender). handleShareWithHo / canShareWithHo /
            sharing / shareSuccess state remain for back-compat with any other caller. */}
        {showAgreementLink && !canResumeLegacyDraft && !canReview && (
          <button
            onClick={openReviewModal}
            className="px-3 py-1.5 text-xs font-medium rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
          >
            {signed ? "View Signed Agreement" : "Agreement Details"}
          </button>
        )}
        {canConvertToRetail && (
          <button
            onClick={() => handleConvertRetail(true)}
            disabled={converting || sharing || savingPreferredDate}
            className="px-3 py-1.5 text-xs font-medium rounded bg-white border border-teal-300 text-teal-700 hover:bg-teal-50 disabled:opacity-50"
            title="Claim denied or partially approved? Switch this deal to a full retail job with a 50/50 payment schedule."
          >
            {converting ? "Converting…" : "🏷 Convert to Retail"}
          </button>
        )}
        {canRevertToInsurance && (
          <button
            onClick={() => handleConvertRetail(false)}
            disabled={converting || sharing || savingPreferredDate}
            className="px-3 py-1.5 text-xs font-medium rounded bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {converting ? "Reverting…" : "Revert to Insurance"}
          </button>
        )}
      </div>

      {/* Stale-agreement banner (Jun 11, 2026) — scope edited after the
          agreement was generated; signing link still shows the old numbers. */}
      {agreementStale && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-lg">
          <div className="text-xs font-semibold text-amber-900 mb-1">
            {onlyTradesStale
              ? "⚠ A trade billed to the homeowner changed since the agreement was generated"
              : staleParts?.trades
                ? "⚠ Scope and trades changed since the agreement was generated"
                : "⚠ Scope changed since the agreement was generated"}
          </div>
          <div className="text-[11px] text-amber-700 mb-2">
            {onlyTradesStale
              ? "The agreement Chad approved doesn't carry the trades as they stand on the trade sheet. Resubmit to refresh the agreement with them and send it back to Chad for approval."
              : "The signing link still shows the old numbers. Resubmit to refresh the agreement with the revised scope and send it back to Chad for approval."}
          </div>
          {resubmitBlocked && scope.status ? (
            <ScopeSubmitBlock
              status={scope.status}
              verb="resubmit"
              busy={resubmitting}
              onRequest={(note) => handleResubmitAfterScopeEdit(note)}
            />
          ) : (
            <button
              onClick={() => handleResubmitAfterScopeEdit()}
              disabled={resubmitting || sharing || savingPreferredDate}
              className="px-3 py-1.5 text-xs font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {resubmitting ? "Resubmitting…" : "Resubmit for approval"}
            </button>
          )}
        </div>
      )}

      {/* Post-sign preferred-date entry — fires Chad's second approval.
          Restyled purple → amber Apr 30, 2026 to match BuildApprovalPrep. */}
      {canEnterPreferredDate && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="text-xs font-semibold text-amber-900 mb-1">Set homeowner's preferred build date</div>
          <div className="text-[11px] text-amber-700 mb-2">
            Agreement signed and colors chosen. Pick the homeowner's preferred build date — Chad will get a text
            and release it to Robert for scheduling.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={preferredDateInput}
              onChange={(e) => setPreferredDateInput(e.target.value)}
              className="text-sm px-2 py-1.5 border border-amber-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <button
              onClick={handleSavePreferredDate}
              disabled={savingPreferredDate || !preferredDateInput}
              className="px-3 py-1.5 text-xs font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {savingPreferredDate ? "Sending to Chad…" : "Send to Chad for release"}
            </button>
          </div>
        </div>
      )}

      {/* Awaiting-Chad-release banner */}
      {awaitingChadRelease && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
          <div className="font-semibold mb-0.5">Waiting on Chad to submit the build</div>
          <div className="text-amber-700">
            HO wants build on{" "}
            {deal.ho_preferred_build_date
              ? new Date((deal.ho_preferred_build_date as string) + "T12:00:00").toLocaleDateString()
              : "(no date set)"}
            . Chad has been notified.
          </div>
        </div>
      )}

      {/* Build submitted: it sits on the Build Approvals board */}
      {buildOnBoard && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
          <div className="font-semibold mb-0.5">Build submitted — waiting for approval</div>
          <div className="text-amber-700">
            It is on the {BUILD_APPROVAL_BOARD}
            {deal.ho_preferred_build_date
              ? `; HO wants build on ${new Date((deal.ho_preferred_build_date as string) + "T12:00:00").toLocaleDateString()}`
              : ""}
            . Chad or Robert approves it there.
            {canApproveBuilds(role, isSuperUser) && (
              <>
                {" "}
                <a href={buildApprovalsLink(role)} className="font-medium underline">Approve on the board →</a>
              </>
            )}
          </div>
        </div>
      )}

      {/* Build approved on the board: Robert schedules it */}
      {buildApproved && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900">
          <div className="font-semibold mb-0.5">Build approved ✓</div>
          <div className="text-emerald-700">
            {deal.build_scheduled_date
              ? `Scheduled for ${new Date((deal.build_scheduled_date as string).slice(0, 10) + "T12:00:00").toLocaleDateString()}.`
              : "Approved on the Build Approvals board. Robert will schedule the crew."}
          </div>
        </div>
      )}

      {/* Agreement released without a build move (legacy path) */}
      {status === "released" && buildState === "before" && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900">
          <div className="font-semibold mb-0.5">Agreement released ✓</div>
          <div className="text-emerald-700">
            Chad released the agreement. The build is submitted from the deal page.
          </div>
        </div>
      )}

      <AgreementReviewModal
        deal={deal}
        open={reviewModalOpen}
        mode={reviewModalMode}
        role={role}
        isSuperUser={isSuperUser}
        member={member}
        onClose={() => setReviewModalOpen(false)}
        onReload={onReload}
        scopeGate={showScopeCheck ? scope : undefined}
      />

      {actionError && (
        <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800">
          {actionError}
        </div>
      )}
      {shareSuccess && !actionError && (
        <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
          Agreement link sent to {deal.homeowner_name} at {formatPhone(deal.homeowner_phone)}.
        </div>
      )}
    </>
  );
}

function StatTile({ label, value, tone = "neutral" }: { label: string; value: number; tone?: "neutral" | "insurance" | "ho" }) {
  const toneCls = {
    neutral: "bg-slate-50",
    insurance: "bg-sky-50 border border-sky-100",
    ho: "bg-emerald-50 border border-emerald-100",
  }[tone];
  const labelCls = {
    neutral: "text-slate-500",
    insurance: "text-sky-700",
    ho: "text-emerald-700",
  }[tone];
  const valueCls = {
    neutral: "text-bardin-ink",
    insurance: "text-sky-900",
    ho: "text-emerald-900",
  }[tone];
  return (
    <div className={`p-2.5 rounded-lg ${toneCls}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wider ${labelCls}`}>{label}</div>
      <div className={`text-base font-semibold ${valueCls}`}>{fmtUSD(value)}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scope Editor Modal — full-screen takeover on mobile, centered on desktop
// ---------------------------------------------------------------------------

interface EditableFields {
  rcv_total: number;
  depreciation_total: number;
  deductible: number;
  /** Legacy single "supplements" field — kept for back-compat with existing
   *  deals. New deals should use the split below. */
  supplements: number;
  /** Apr 30, 2026: split — what Bardin invoiced the carrier (pending). */
  supplement_invoice_amount: number;
  /** Apr 30, 2026: split — what the carrier approved (final). */
  supplement_approved_amount: number;
  pwi_code: number;
  items_not_performing_lines: string;
  acv_credit: number;
  dep_credit: number;
  upgrades: number;
  retail_profit: number;
  at_cost_retail: number;
  // Oct 2, 2026 — add-on notes (customer description + internal note).
  upgrades_description: string;
  upgrades_internal_note: string;
  retail_profit_description: string;
  retail_profit_internal_note: string;
  at_cost_retail_description: string;
  at_cost_retail_internal_note: string;
  misc_credit: number;
  non_commissionable: number;
  acv_only: boolean;
}

function toNum(v: unknown): number {
  if (typeof v === "number") return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** The scope editor's fields as the deal has them saved. */
function editorFieldsOf(deal: Deal): EditableFields {
  return {
    rcv_total: toNum(deal.rcv_total),
    depreciation_total: toNum(deal.depreciation_total),
    deductible: toNum(deal.deductible),
    supplements: toNum(deal.supplements),
    supplement_invoice_amount: toNum((deal as { supplement_invoice_amount?: number }).supplement_invoice_amount),
    supplement_approved_amount: toNum((deal as { supplement_approved_amount?: number }).supplement_approved_amount),
    pwi_code: toNum(deal.pwi_code),
    items_not_performing_lines: (deal.items_not_performing_lines as string) ?? "",
    acv_credit: toNum(deal.acv_credit),
    dep_credit: toNum(deal.dep_credit),
    upgrades: toNum(deal.upgrades),
    retail_profit: toNum(deal.retail_profit),
    at_cost_retail: toNum(deal.at_cost_retail),
    upgrades_description: (deal.upgrades_description as string) ?? "",
    upgrades_internal_note: (deal.upgrades_internal_note as string) ?? "",
    retail_profit_description: (deal.retail_profit_description as string) ?? "",
    retail_profit_internal_note: (deal.retail_profit_internal_note as string) ?? "",
    at_cost_retail_description: (deal.at_cost_retail_description as string) ?? "",
    at_cost_retail_internal_note: (deal.at_cost_retail_internal_note as string) ?? "",
    misc_credit: toNum(deal.misc_credit),
    non_commissionable: toNum(deal.non_commissionable),
    acv_only: deal.acv_only === true,
  };
}

interface ScopeEditorModalProps {
  deal: Deal;
  open: boolean;
  role: string | undefined;
  isSuperUser: boolean;
  /** Apr 30, 2026: needed to write deal_documents row on source-doc upload. */
  member: TeamMember | null;
  onClose: () => void;
  onSaved: (updated: Partial<Deal>) => void;
}

/**
 * Dispatcher (Jun 11, 2026): retail deals get the lightweight retail editor
 * (contract total + squares + description with the 50/50 schedule preview);
 * everything else gets the full insurance scope worksheet. Two separate
 * components so each keeps its own hook tree.
 */
export function ScopeEditorModal(props: ScopeEditorModalProps) {
  if (props.deal.is_retail === true) return <RetailScopeEditorModal {...props} />;
  return <InsuranceScopeEditorModal {...props} />;
}

// ---------------------------------------------------------------------------
// Retail Scope Editor (Jun 11, 2026) — fixed contract total, RM-entered
// squares, free-text scope description. Live preview of the 50/50 payment
// schedule and the over-base commission pool (contract − sq × $460 base).
// ---------------------------------------------------------------------------

// Build the initial line-item list for the retail editor. Uses saved
// retail_items when present; otherwise seeds a single roof line from the legacy
// retail_contract_total so deals converted before Jun 17, 2026 open cleanly.
function seedRetailItems(deal: Deal): RetailLineItem[] {
  const saved = (deal as { retail_items?: RetailLineItem[] | null }).retail_items;
  if (Array.isArray(saved) && saved.length > 0) {
    return saved.map((it) => ({
      id: it.id || (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
      label: it.label ?? "",
      kind: it.kind === "at_cost" || it.kind === "normal" ? it.kind : "roof",
      amount: Number(it.amount) || 0,
    }));
  }
  return [
    {
      id: crypto.randomUUID ? crypto.randomUUID() : "roof",
      label: "Roof replacement",
      kind: "roof",
      amount: toNum(deal.retail_contract_total),
    },
  ];
}

function RetailScopeEditorModal({ deal, open, member, onClose, onSaved }: ScopeEditorModalProps) {
  const [items, setItems] = useState<RetailLineItem[]>(() => seedRetailItems(deal));
  const [squares, setSquares] = useState<number>(() => toNum(deal.retail_squares));
  const [description, setDescription] = useState<string>(() => deal.retail_scope_description ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setItems(seedRetailItems(deal));
      setSquares(toNum(deal.retail_squares));
      setDescription(deal.retail_scope_description ?? "");
      setErr(null);
    }
  }, [open, deal]);

  // Lock body scroll while open (same pattern as the insurance editor)
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  function patchItem(id: string, patch: Partial<RetailLineItem>) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }
  function addItem() {
    setItems((prev) => [
      ...prev,
      { id: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()), label: "", kind: "normal", amount: 0 },
    ]);
  }
  function removeItem(id: string) {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }

  const contractTotal = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const atCostTotal = items.filter((it) => it.kind === "at_cost").reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const normalTotal = items.filter((it) => it.kind === "normal").reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const calc = calcScope({
    ...deal,
    retail_contract_total: contractTotal,
    retail_squares: squares,
    retail_at_cost_total: atCostTotal,
  });

  const others = items.filter((it) => it.kind !== "roof");
  const roofItem = items.find((it) => it.kind === "roof");
  const roofAmount = Number(roofItem?.amount) || 0;
  // Oct 29, 2026 — trades billed to the homeowner from the trade sheet: on top
  // of the contract (not a line item here), due at completion; their upside
  // above the 30% floor joins the pool. calc reads them off the deal row.
  const trades = tradeMoneyOf(calc);
  const showTradeSplit = seesTradeSplit(member);

  async function handleSave() {
    if (contractTotal <= 0) { setErr("Enter at least the roof line amount."); return; }
    setSaving(true);
    setErr(null);
    // Normalize labels so blank rows still print sensibly on the agreement.
    const cleaned: RetailLineItem[] = items.map((it) => ({
      id: it.id,
      kind: it.kind,
      amount: Number(it.amount) || 0,
      label: it.label.trim() || (it.kind === "roof" ? "Roof replacement" : it.kind === "at_cost" ? "At-cost item" : "Additional work"),
    }));
    const payload: Partial<Deal> = {
      retail_items: cleaned,
      retail_at_cost_total: atCostTotal,
      retail_contract_total: contractTotal,
      retail_squares: squares,
      retail_scope_description: description.trim() || null,
      // Keep the generic invoice/balance machinery honest: the retail contract
      // total IS the invoice. final_invoice_amount feeds the balance recompute
      // trigger + Shannon's AR; total_contract_amount feeds the portal
      // balanceKnown guards. Oct 29, 2026: calc.initialInvoice is the contract +
      // the trades billed to the homeowner (calcScope), the same stamp SQL
      // trade_sync_deal refreshes when a trade changes.
      initial_invoice_amount: calc.initialInvoice,
      final_invoice_amount: calc.initialInvoice,
      total_contract_amount: calc.initialInvoice,
      homeowner_oop: calc.initialInvoice,
      // No carrier scope document exists on a retail job — flip the gate flag
      // so the install_meeting → build_submitted transition (which requires
      // "insurance paperwork uploaded") doesn't dead-end the deal.
      insurance_paperwork_uploaded: true,
    };
    const { error } = await supabase.from("deals").update(payload).eq("id", deal.id);
    if (error) {
      setErr(`Save failed: ${error.message}`);
      setSaving(false);
      return;
    }
    onSaved(payload);
    setSaving(false);
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-slate-50 w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[92vh] sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-500">Scope Worksheet</div>
            <div className="text-base font-semibold text-bardin-ink flex items-center gap-2">
              Retail Scope
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-teal-100 text-teal-800">🏷 Retail · 50/50</span>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 -mr-2 text-slate-500 hover:text-bardin-ink rounded-lg hover:bg-slate-100 disabled:opacity-40"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {deal.install_agreement_signed === true && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <span className="text-base leading-none">⚠</span>
              <span>
                <span className="font-semibold">This agreement is already signed.</span>{" "}
                Editing these numbers updates the record but does not change the PDF the homeowner signed.
              </span>
            </div>
          )}

          {/* ── Line items ──────────────────────────────────────────────── */}
          <Card title="Line Items" subtitle="The roof line plus any add-ons. Mark add-ons at-cost to exclude them from commission. Gutters go on the trade sheet now (billed from there, due at completion).">
            {/* Roof line */}
            {roofItem && (
              <div className="mb-3 rounded-lg border border-teal-200 bg-teal-50/40 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-base leading-none">🏠</span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-teal-800">Roof (uses squares × base)</span>
                </div>
                <input
                  type="text"
                  value={roofItem.label}
                  onChange={(e) => patchItem(roofItem.id, { label: e.target.value })}
                  placeholder="Roof replacement"
                  className="w-full mb-2 px-2 py-1.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-bardin-teal/30"
                />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-500">Roof price</span>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={roofItem.amount === 0 ? "" : roofItem.amount}
                      onChange={(e) => patchItem(roofItem.id, { amount: Number(e.target.value) || 0 })}
                      className="w-32 pl-7 pr-2 py-1.5 text-right bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-bardin-teal/30"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <span className="text-xs text-slate-500">Squares <span className="text-slate-400">({fmtUSD(calc.retailBasePerSquare)}/sq base)</span></span>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    value={squares === 0 ? "" : squares}
                    onChange={(e) => setSquares(Number(e.target.value) || 0)}
                    className="w-32 px-2 py-1.5 text-right bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-bardin-teal/30"
                  />
                </div>
              </div>
            )}

            {/* Other items */}
            {others.map((it) => (
              <div key={it.id} className="mb-2 rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={it.label}
                    onChange={(e) => patchItem(it.id, { label: e.target.value })}
                    placeholder="e.g., Skylight replacement"
                    className="flex-1 min-w-0 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-bardin-teal/30"
                  />
                  <button
                    onClick={() => removeItem(it.id)}
                    className="p-1.5 text-slate-400 hover:text-rose-600 rounded hover:bg-rose-50"
                    aria-label="Remove item"
                    title="Remove item"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  {/* Commission kind toggle */}
                  <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
                    <button
                      onClick={() => patchItem(it.id, { kind: "normal" })}
                      className={`px-2.5 py-1 font-medium ${it.kind === "normal" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    >
                      Normal
                    </button>
                    <button
                      onClick={() => patchItem(it.id, { kind: "at_cost" })}
                      className={`px-2.5 py-1 font-medium ${it.kind === "at_cost" ? "bg-slate-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    >
                      At cost
                    </button>
                  </div>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={it.amount === 0 ? "" : it.amount}
                      onChange={(e) => patchItem(it.id, { amount: Number(e.target.value) || 0 })}
                      className="w-32 pl-7 pr-2 py-1.5 text-right bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-bardin-teal/30"
                    />
                  </div>
                </div>
                <div className="mt-1 text-[10px] text-slate-400">
                  {it.kind === "at_cost" ? "At cost — no commission, but still billed in the contract total." : "Normal — full amount counts toward commission."}
                </div>
              </div>
            ))}

            <button
              onClick={addItem}
              className="mt-1 w-full px-3 py-2 text-sm font-medium text-bardin-teal border border-dashed border-teal-300 rounded-lg hover:bg-teal-50"
            >
              + Add item
            </button>
          </Card>

          <Card title="Scope of Work" subtitle="What Bardin is doing — prints on the agreement and the homeowner sees it at signing.">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Full roof replacement — tear-off, synthetic underlayment, OC Duration shingles, ridge vent, new pipe boots and flashing."
              className="w-full text-sm p-2 border border-slate-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-bardin-teal/30"
              rows={4}
            />
          </Card>

          {/* Oct 29, 2026 — trades billed to the homeowner (read-only: the trade
              sheet is where they are priced). */}
          {trades.total > 0 && (
            <Card title="Trades billed to the homeowner" subtitle="From the trade sheet: billed on top of the contract total, due at completion. Change them on the trade sheet.">
              <TradesBilledRows trades={trades} showSplit={showTradeSplit} signed={deal.install_agreement_signed === true} bordered={false} />
            </Card>
          )}

          {/* 50/50 schedule preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Card title="50% Deposit (Before Build)" dotColor="sky" subtitle="Due at signing / before material delivery.">
              <TotalBar label="Deposit" value={calc.retailDeposit} tone="insurance" />
            </Card>
            <Card
              title={trades.total > 0 ? "Due on Completion (50% + trades)" : "50% Due on Completion"}
              dotColor="emerald"
              subtitle={trades.total > 0 ? "The other 50% + the trades billed to the homeowner, due upon substantial completion." : "Due upon substantial completion of the work."}
            >
              <TotalBar label="Final Payment" value={calc.retailCompletionPayment} tone="ho" />
            </Card>
          </div>

          {/* Commission pool */}
          <div className="bg-slate-900 text-white rounded-xl p-4 mb-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Commission Pool</div>
            <DarkRow label="Contract total" value={calc.retailContractTotal} />
            <DarkRow label={`− Base (${calc.retailSquares} sq × ${fmtUSD(calc.retailBasePerSquare)}/sq)`} value={-calc.retailBaseTotal} rose />
            {atCostTotal > 0 && <DarkRow label="− At-cost items" value={-atCostTotal} rose />}
            {/* Oct 29, 2026: the trades' price above the 30% floor joins the pool
                ("all that works just like retail commissions"); the floor is at cost. */}
            {showTradeSplit && trades.upside > 0 && <DarkRow label="+ Trade upside (above the 30% floor)" value={trades.upside} />}
            {calc.punchListRmCost > 0 && <DarkRow label="− Punch list RM cost" value={-calc.punchListRmCost} rose />}
            <div className="mt-2 flex items-center justify-between px-3 py-2 bg-emerald-500/20 border-2 border-emerald-500 rounded-lg">
              <div className="text-sm font-semibold uppercase tracking-wide">Pool (commissionable)</div>
              <div className="text-lg font-bold">{fmtUSD(calc.commissionable)}</div>
            </div>
            <div className="mt-2 text-[10px] text-slate-400">
              Roof ({fmtUSD(roofAmount)}) + normal add-ons ({fmtUSD(normalTotal)}) − base{trades.total > 0 ? " + the trades' upside above the floor" : ""}. At-cost items earn no commission. RM / TL / ASM split is set per job by Chad on the Commission board.
            </div>
          </div>

          {err && (
            <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800">{err}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-3 bg-white border-t border-slate-200">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium bg-bardin-teal text-white rounded-lg hover:bg-bardin-teal/90 disabled:opacity-50">
            {saving ? "Saving…" : "Save Retail Scope"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InsuranceScopeEditorModal({
  deal,
  open,
  role,
  isSuperUser,
  member,
  onClose,
  onSaved,
}: ScopeEditorModalProps) {
  // Same role gate as ScopeSummaryCard — RM/TL/ASM can't see/edit supplements
  const canSeeSupplements = isSuperUser || role === "admin" || role === "sales_director" || role === "finance";

  // Source-doc upload state. Apr 30, 2026: insurance paperwork == insurance
  // scope (per Greg) — they're the same document. Uploading the source PDF
  // and entering the numbers transcribed from it both happen in this modal.
  // The file is staged on the client and uploaded inside handleSave() so we
  // can roll back the scope save if upload fails.
  const [scopeFile, setScopeFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Decoupled from scopeFile so a partial-success retry (upload + doc + note
  // succeeded, deals.update failed) doesn't double-upload on the next save —
  // we clear scopeFile after Step 1 success and remember the flag-flip intent
  // here. Reset on modal open and on full save success.
  const [pendingFlagFlip, setPendingFlagFlip] = useState(false);

  const [fields, setFields] = useState<EditableFields>(() => editorFieldsOf(deal));
  const [saving, setSaving] = useState(false);

  // Oct 30, 2026 (scope gate) — once the carrier's scope has been read and the
  // read found its lines, the un-performed credits come from the carrier lines
  // Bardin won't do, ticked in the picker, instead of typed totals. Trigger
  // deals_apply_scope_picks sets acv_credit, dep_credit and the line text from
  // the picks when this saves. Unsigned insurance deals only.
  const pickEligible =
    deal.is_retail !== true &&
    deal.install_agreement_signed !== true &&
    !POST_AGREEMENT_STAGES.has((deal.stage as string) ?? "");
  const editorScope = useScopeGate(open && pickEligible ? deal.id : null, scopeGateRefreshKey(deal));
  const editorRead = editorScope.status?.read ?? null;
  const pickReadId =
    editorRead &&
    (editorRead.line_count ?? 0) > 0 &&
    ["match", "mismatch", "flagged"].includes(editorScope.status?.state ?? "")
      ? editorRead.id
      : null;
  const [picks, setPicks] = useState<PickedLine[]>(() => dealPicks(deal));
  const [picksTouched, setPicksTouched] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Reset to what the deal has saved when the editor opens, and when this deal's scope changes underneath it
  // (someone else saved it). Keyed on the saved values, not the deal object: the deal page re-reads the deal on
  // every realtime event it hears (company-wide for Alex and Chad), and an unchanged deal must not wipe what is
  // being typed or the lines being picked, or close the picker (second review, Oct 30, 2026).
  const dealRef = useRef(deal);
  dealRef.current = deal;
  const savedKey = JSON.stringify([deal.id, editorFieldsOf(deal), dealPicks(deal)]);
  useEffect(() => {
    if (!open) return;
    const d = dealRef.current;
    setFields(editorFieldsOf(d));
    setScopeFile(null);
    setUploadError(null);
    setPendingFlagFlip(false);
    setPicks(dealPicks(d));
    setPicksTouched(false);
    setPickerOpen(false);
  }, [open, savedKey]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Gate supplements out of the displayed totals for non-privileged roles.
  // Value in DB is preserved (not editable by these roles), so admin's view stays correct.
  const calc = calcScope({
    ...deal,
    ...fields,
    supplements: canSeeSupplements ? fields.supplements : 0,
    supplement_invoice_amount: canSeeSupplements ? fields.supplement_invoice_amount : 0,
    supplement_approved_amount: canSeeSupplements ? fields.supplement_approved_amount : 0,
  });

  // Oct 29, 2026 — trades billed to the homeowner, read off the deal row (the
  // trade sheet writes them; nothing here edits them).
  const trades = tradeMoneyOf(calc);
  const showTradeSplit = seesTradeSplit(member);
  // The bucket the Commission board deducts (review of the release, Oct 30): a
  // grandfathered deal's non-collect stays out of it (non_collect_deduct_active
  // false), so its summary never shows a deduction the board doesn't take.
  const ncDeductActive = (deal as { non_collect_deduct_active?: boolean | null }).non_collect_deduct_active !== false;
  const ncOver = nonCollectBucket(calc.nonCollectRevenue, ncDeductActive, trades.marginLoss).over;

  function setField<K extends keyof EditableFields>(key: K, value: EditableFields[K]) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  // The picker's lines become the credits on screen right away; Save writes
  // them (with the picks) in the same update as the numbers.
  function applyPicks(next: PickedLine[]) {
    const t = pickTotals(next);
    setPicks(next);
    setPicksTouched(true);
    setFields((prev) => ({
      ...prev,
      acv_credit: t.acv,
      dep_credit: t.dep,
      items_not_performing_lines: pickedLinesText(next) ?? "",
    }));
  }
  const pickMode = !!pickReadId || picks.length > 0;
  const pickedTotals = pickTotals(picks);
  const typedCredits = picks.length === 0 && (fields.acv_credit > 0 || fields.dep_credit > 0);

  async function handleSave() {
    setSaving(true);
    setUploadError(null);

    // Step 1: upload the source document if a NEW file is staged. We do the
    // upload BEFORE saving scope numbers so the user doesn't end up with
    // numbers saved but the document missing — if this fails, the modal stays
    // open with an error and the RM can retry.
    //
    // On success we clear scopeFile and set pendingFlagFlip so a partial-
    // success retry (upload OK, deals.update fails) doesn't re-upload the
    // same file on the next save attempt.
    if (scopeFile && member) {
      // Refresh session before upload to prevent expired-token errors on mobile.
      await supabase.auth.refreshSession();

      // Convert iPhone HEIC/HEIF to JPEG so the scope photo is viewable on
      // every device/browser (HEIC won't render in Chrome/Windows). No-op
      // for PDFs and other image types.
      const upFile = await heicToJpeg(scopeFile);

      const safeName = upFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${deal.id}/${Date.now()}-${safeName}`;
      const fileName = upFile.name;
      const fileType = upFile.type;
      const fileSize = upFile.size;

      let uploadErr: { message: string } | null = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        const { error } = await supabase.storage
          .from("deal-documents")
          .upload(path, upFile, { contentType: fileType || "application/octet-stream" });
        if (!error) { uploadErr = null; break; }
        uploadErr = error;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 1000));
      }

      if (uploadErr) {
        const hint = uploadErr.message.includes("Load failed")
          ? "Upload failed — your session may have expired. Sign out and back in, then retry."
          : `Upload failed: ${uploadErr.message}`;
        setUploadError(hint);
        setSaving(false);
        return;
      }

      const { error: docErr } = await supabase.from("deal_documents").insert({
        deal_id: deal.id,
        file_name: fileName,
        file_type: "insurance_scope",
        storage_path: path,
        file_size: fileSize,
        mime_type: fileType,
        uploaded_by: member.id,
      });
      if (docErr) {
        // The blob made it to the bucket but the DB row didn't land — that's
        // an orphan. Best-effort cleanup so we don't accumulate untracked
        // files in storage. We don't await/check the result; if delete also
        // fails, the user's retry will create a new blob anyway and the old
        // orphan is a known acceptable cost.
        void supabase.storage.from("deal-documents").remove([path]);
        setUploadError(`Document record failed: ${docErr.message}`);
        setSaving(false);
        return;
      }

      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member.id,
        note_type: "upload",
        content: `Uploaded ${fileName} (Insurance Scope)`,
        metadata: { file_name: fileName, file_type: "insurance_scope", from: "scope_editor" },
      });

      // Upload + side-effects committed. Clear the staged file so a Step-2
      // retry doesn't re-upload, but remember we owe a flag flip.
      setScopeFile(null);
      setPendingFlagFlip(true);
    }

    // Step 2: save scope numbers (existing logic).
    // Legacy fields use the full calc (with supplements) regardless of role — DB
    // should always hold the true numbers; UI gating is a display concern only.
    const fullCalc = calcScope({ ...deal, ...fields });
    const computedAcv = fields.rcv_total - fields.depreciation_total;
    // retail_trades_total is the old lumped field still read by CommissionBoard,
    // PipelineView, etc. Keep it in sync with the new split fields until those
    // views are migrated. Drop target ~June 1, 2026 after Shannon reconciliation
    // window. (AgreementTab was deleted May 7 — flows moved into ScopeSection.)
    const rolledRetailTrades = fields.upgrades + fields.retail_profit + fields.at_cost_retail;
    const payload: Partial<Deal> = {
      ...fields,
      // Normalize empty description to null (matches existing DB convention)
      items_not_performing_lines: fields.items_not_performing_lines.trim() || null,
      upgrades_description: fields.upgrades_description.trim() || null,
      upgrades_internal_note: fields.upgrades_internal_note.trim() || null,
      retail_profit_description: fields.retail_profit_description.trim() || null,
      retail_profit_internal_note: fields.retail_profit_internal_note.trim() || null,
      at_cost_retail_description: fields.at_cost_retail_description.trim() || null,
      at_cost_retail_internal_note: fields.at_cost_retail_internal_note.trim() || null,
      acv_amount: computedAcv,
      rcv_credit: fields.acv_credit + fields.dep_credit,
      retail_trades_total: rolledRetailTrades,
      initial_invoice_amount: fullCalc.initialInvoice,
      // Jul 14, 2026 (Invoice Derivation Phase 1): final_invoice_amount is no
      // longer written here. For lifecycle-stage deals with real scope the DB
      // trigger derives it (gross − non-collect) on this very save — the old
      // client write stamped the GROSS number and, during a pending supplement,
      // baked the REQUESTED amount into the charged balance (Naylor/Poma).
      // Pre-supps deals simply carry no final until they enter the lifecycle.
      homeowner_oop: fullCalc.hoOutOfPocket,
      net_deductible: Math.max(0, fields.deductible - fullCalc.creditsApplied),
      acv_only: fields.acv_only,
    };
    // If a source doc was uploaded this session (either just now in Step 1 or
    // on a prior partial-success attempt), flip the gate flag so build_submitted's
    // insurance_paperwork_uploaded requirement resolves. Never UNSET this — the
    // flag may have been set previously through the waiting_on_claims flow.
    // We check `pendingFlagFlip || scopeFile` so the JUST-uploaded case (where
    // setPendingFlagFlip is in flight) is also covered.
    if (pendingFlagFlip || scopeFile) {
      (payload as { insurance_paperwork_uploaded?: boolean }).insurance_paperwork_uploaded = true;
    }
    if (picksTouched) {
      payload.items_not_performing_detail = picks.length > 0 ? picks : null;
    }
    // If the user can't see supplements, don't let their save overwrite whatever
    // admin may have already set. Preserve DB value by dropping from the payload.
    if (!canSeeSupplements) {
      delete payload.supplements;
      delete (payload as { supplement_invoice_amount?: number }).supplement_invoice_amount;
      delete (payload as { supplement_approved_amount?: number }).supplement_approved_amount;
    }
    const { error } = await supabase.from("deals").update(payload).eq("id", deal.id);
    if (error) {
      // Step 2 failed but Step 1 may have completed. scopeFile is already null
      // and pendingFlagFlip is true, so the next Save click will skip the
      // upload and just retry the deals.update with the gate flag.
      alert(`Save failed: ${error.message}`);
      setSaving(false);
      return;
    }
    // Full success — reset both the file and the flag-flip intent.
    setScopeFile(null);
    setPendingFlagFlip(false);
    onSaved(payload);
    setSaving(false);
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-slate-50 w-full sm:max-w-4xl h-full sm:h-auto sm:max-h-[92vh] sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-500">Scope Worksheet</div>
            <div className="text-base font-semibold text-bardin-ink">Insurance Scope</div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 -mr-2 text-slate-500 hover:text-bardin-ink rounded-lg hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            aria-label="Close"
            title={saving ? "Wait for save to finish" : "Close"}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {/* Post-sign edit warning (May 20, 2026). Privileged roles can edit a
              signed deal's financials, but the homeowner-signed PDF won't change. */}
          {deal.install_agreement_signed === true && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <span className="text-base leading-none">⚠</span>
              <span>
                <span className="font-semibold">This agreement is already signed.</span>{" "}
                Editing these numbers updates the record and recomputes commissions/balances,
                but it does <span className="font-semibold">not</span> change the PDF the
                homeowner signed. Use this for corrections, final-invoice reconciliation, or
                supplement true-ups only.
              </span>
            </div>
          )}
          {/* Scope totals bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <StatTile label="RCV" value={calc.rcv} />
            <StatTile label="ACV" value={calc.acv} />
            <StatTile label="Recoverable Depreciation" value={calc.dep} />
            <StatTile label="Deductible" value={calc.deductible} />
          </div>

          {/* Source document — Apr 30, 2026.
              The carrier's scope PDF + the numbers transcribed from it are the
              same artifact. Upload here, then transcribe below. Optional on
              edit (the doc may already be on file from a prior save / from
              the legacy waiting_on_claims flow); required for first save on
              install_meeting deals to flip the build_submitted gate. */}
          <Card title="Source document">
            <div className="space-y-2 -mt-1">
              {/* "Already on file" hint shows whenever a doc exists, including
                  while staging a replacement — keeps the RM aware they're
                  about to replace the existing doc, not add a fresh one. */}
              {deal.insurance_paperwork_uploaded && (
                <div className="text-xs text-emerald-700 font-medium flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center font-bold">✓</span>
                  {scopeFile
                    ? "A scope document is already on file. Saving will add the new one alongside it."
                    : "Scope document already on file. Upload a new one to replace it."}
                </div>
              )}
              {scopeFile ? (
                <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs">
                  <span>📎</span>
                  <span className="font-medium text-emerald-900 truncate flex-1">{scopeFile.name}</span>
                  <span className="text-emerald-700 whitespace-nowrap">{(scopeFile.size / 1024).toFixed(0)} KB</span>
                  <button
                    onClick={() => setScopeFile(null)}
                    className="text-emerald-700 hover:text-emerald-900 font-bold ml-1 px-1"
                    aria-label="Remove file"
                    type="button"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <label className="block border-2 border-dashed border-slate-300 rounded-lg p-3 text-center cursor-pointer hover:border-bardin-teal hover:bg-bardin-teal/5 transition">
                  <div className="text-xs font-semibold text-slate-700">
                    {deal.insurance_paperwork_uploaded ? "Replace scope document" : "Upload the carrier's scope document"}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    PDF or image — the source you're transcribing below
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.heic,.tiff,.doc,.docx"
                    onChange={(e) => setScopeFile(e.target.files?.[0] ?? null)}
                    disabled={saving}
                  />
                </label>
              )}
              {uploadError && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                  {uploadError}
                </div>
              )}
            </div>
          </Card>

          {/* Carrier scope inputs */}
          <Card title="Carrier Scope">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <NumField label="RCV" value={fields.rcv_total} onChange={(v) => setField("rcv_total", v)} />
              <NumField label="Recoverable Depreciation" value={fields.depreciation_total} onChange={(v) => setField("depreciation_total", v)} />
              <NumField label="Deductible" value={fields.deductible} onChange={(v) => setField("deductible", v)} />
              {canSeeSupplements && (
                <>
                  <NumField
                    label="Supplement Amount Requested"
                    value={fields.supplement_invoice_amount}
                    onChange={(v) => setField("supplement_invoice_amount", v)}
                    hint="The ADDED amount requested from the carrier — not Apex's revised estimate total. Not shown to the homeowner until approved."
                  />
                  <NumField
                    label="Approved Supplement Amount"
                    value={fields.supplement_approved_amount}
                    onChange={(v) => setField("supplement_approved_amount", v)}
                    hint="The added amount the carrier approved (final — this is what lands on the homeowner's invoice)"
                  />
                  <div className="sm:col-span-2">
                    <SupplementEntryWarning
                      entered={fields.supplement_invoice_amount}
                      rcv={fields.rcv_total}
                      onUse={(v) => setField("supplement_invoice_amount", v)}
                    />
                  </div>
                </>
              )}
              <NumField label="PWI" value={fields.pwi_code} onChange={(v) => setField("pwi_code", v)} hint="Absorbed by Bardin if denied" />
            </div>
            {/* ACV-only toggle */}
            <label className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={fields.acv_only}
                onChange={(e) => setField("acv_only", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-400"
              />
              <span className="text-sm text-amber-900">
                <span className="font-semibold">ACV only</span>
                <span className="block text-xs text-amber-700 mt-0.5">
                  Insurance is only paying ACV — no recoverable depreciation. Depreciation Settlement = $0.
                </span>
              </span>
            </label>
          </Card>

          {/* Un-performed */}
          {pickMode ? (
            <Card title="Work Bardin won't do" tone="amber" subtitle="Carrier lines left with the homeowner, ticked from the carrier's scope. Their ACV credits the deductible; their depreciation isn't collected from insurance.">
              {picks.length > 0 ? (
                <div className="rounded-lg bg-white border border-amber-100 p-2 mb-2">
                  <PickedLinesList picks={picks} limit={8} />
                </div>
              ) : typedCredits ? (
                <div className="rounded-lg bg-amber-100/60 border border-amber-200 p-2 mb-2 text-xs text-amber-900">
                  Typed by hand: ACV {fmtUSD(fields.acv_credit)} · DEP {fmtUSD(fields.dep_credit)}
                  {fields.items_not_performing_lines.trim() ? ` (${fields.items_not_performing_lines.trim()})` : ""}.
                  {" "}Now that the carrier's scope has been read, the credits come from the lines you tick. Pick them, or pick none to clear these.
                </div>
              ) : (
                <div className="text-xs text-slate-600 mb-2">Nothing left with the homeowner.</div>
              )}
              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
                  <div className="text-[11px] text-slate-500">ACV credited to the deductible</div>
                  <div className="font-semibold text-bardin-ink">{fmtUSD(picks.length > 0 ? pickedTotals.acv : fields.acv_credit)}</div>
                </div>
                <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
                  <div className="text-[11px] text-slate-500">Depreciation not collected</div>
                  <div className="font-semibold text-bardin-ink">{fmtUSD(picks.length > 0 ? pickedTotals.dep : fields.dep_credit)}</div>
                </div>
              </div>
              {pickReadId ? (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  disabled={saving}
                  className="px-3 py-1.5 text-xs font-semibold rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {picks.length > 0 ? "Change the lines" : "Pick lines"}
                </button>
              ) : (
                <div className="text-[11px] text-slate-500">The carrier's scope read isn't available right now, so these lines can't be changed here.</div>
              )}
              {picksTouched && <div className="mt-1.5 text-[11px] text-amber-800">Not saved yet: press Save to keep these lines.</div>}
            </Card>
          ) : (
            <Card title="Un-performed Line Items" tone="amber" subtitle="Scope items Bardin won't perform. ACV portion credits the deductible (homeowner side). DEP just reduces what we collect from insurance.">
              <textarea
                value={fields.items_not_performing_lines}
                onChange={(e) => setField("items_not_performing_lines", e.target.value)}
                placeholder="List the line items that won't be performed (e.g., interior stain, fascia paint)"
                className="w-full text-sm p-2 border border-slate-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 mb-2"
                rows={2}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                <NumField label="Un-performed ACV total" value={fields.acv_credit} onChange={(v) => setField("acv_credit", v)} />
                <NumField label="Un-performed DEP total" value={fields.dep_credit} onChange={(v) => setField("dep_credit", v)} />
              </div>
            </Card>
          )}
          {pickerOpen && pickReadId && (
            <ScopeLinePicker
              deal={{ ...deal, ...fields }}
              readId={pickReadId}
              initialPicks={picks}
              saveLabel="Use these lines"
              onClose={() => setPickerOpen(false)}
              onSave={(next) => { applyPicks(next); }}
            />
          )}

          {/* HO-side inputs */}
          <Card title="Homeowner-side">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <NumField label="Upgrades" value={fields.upgrades} onChange={(v) => setField("upgrades", v)} hint="Shingle upgrade, ridge vent, decking" />
              <AddOnNoteFields
                show={fields.upgrades > 0 || !!fields.upgrades_description || !!fields.upgrades_internal_note}
                description={fields.upgrades_description}
                internalNote={fields.upgrades_internal_note}
                onDescription={(v) => setField("upgrades_description", v)}
                onInternalNote={(v) => setField("upgrades_internal_note", v)}
                placeholder="e.g. Upgrade to OC Duration Designer shingles"
              />
              <NumField label="Retail profit add-ons" value={fields.retail_profit} onChange={(v) => setField("retail_profit", v)} hint="Cash work with margin" />
              <AddOnNoteFields
                show={fields.retail_profit > 0 || !!fields.retail_profit_description || !!fields.retail_profit_internal_note}
                description={fields.retail_profit_description}
                internalNote={fields.retail_profit_internal_note}
                onDescription={(v) => setField("retail_profit_description", v)}
                onInternalNote={(v) => setField("retail_profit_internal_note", v)}
                placeholder="e.g. Replace 3 rotted fascia boards on the back side"
              />
              <NumField label="@ cost retail" value={fields.at_cost_retail} onChange={(v) => setField("at_cost_retail", v)} hint="Other at-cost extras not on the trade sheet (gutters go on the trade sheet now)" />
              <AddOnNoteFields
                show={fields.at_cost_retail > 0 || !!fields.at_cost_retail_description || !!fields.at_cost_retail_internal_note}
                description={fields.at_cost_retail_description}
                internalNote={fields.at_cost_retail_internal_note}
                onDescription={(v) => setField("at_cost_retail_description", v)}
                onInternalNote={(v) => setField("at_cost_retail_internal_note", v)}
                placeholder="e.g. Chimney cap from the sheet-metal shop"
              />
              {/* Oct 29, 2026 (D10) — the trades billed to the homeowner come from
                  the trade sheet, read-only here; nobody types them again. */}
              {trades.total > 0 && (
                <div className="sm:col-span-2">
                  <TradesBilledRows trades={trades} showSplit={showTradeSplit} signed={deal.install_agreement_signed === true} />
                </div>
              )}
              {fields.at_cost_retail > 0 && trades.total > 0 && (
                <div className="sm:col-span-2 -mt-1 mb-1">
                  <AtCostAndTradesNote atCost={fields.at_cost_retail} tradesTotal={trades.total} inEditor />
                </div>
              )}
              <NumField
                label="Non-Collect Revenue"
                value={fields.non_commissionable}
                onChange={(v) => setField("non_commissionable", v)}
                hint="Contracted scope Bardin won't pursue collections on. HO acknowledges at signing. Shares one bucket with any trade shortfalls Chad deducts: the first $500 is forgiven, the rest is deducted from RM + TL commission (75/25 default, Chad-editable)."
              />
              {/* Misc Credit moved to Shannon's Finance area post-build (May 15, 2026).
                  Shown here read-only so RMs see context but can't edit. */}
              <ReadOnlyField
                label="Misc Credit"
                value={fields.misc_credit}
                note="Set by Shannon in Finance Dashboard post-build (goodwill / referral / deductible assist)."
              />
            </div>
          </Card>

          {/* Ledger — First Payment (left) + Depreciation Settlement (right) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Card
              title="First Payment to Bardin (Before Build)"
              dotColor="sky"
              subtitle="Insurance sends ACV check to homeowner. HO endorses to Bardin and pays out-of-pocket before construction."
            >
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">ACV Settlement (Insurance → HO → Bardin)</div>
              <CalcRow label="ACV" value={calc.acv} indent />
              <CalcRow label="− Deductible" value={-calc.deductible} indent negative />
              <CalcRow label="ACV Settlement (endorsed to Bardin)" value={calc.acvSettlementToBardin} bold />
              <div className="border-t border-slate-100 my-2"></div>
              <CalcRow label="+ HO Out-of-Pocket" value={calc.hoOutOfPocket} indent />
              <TotalBar label="First Payment to Bardin" value={calc.firstPaymentToBardin} tone="insurance" />
              <div className="mt-2 text-[10px] text-slate-500">
                HO endorses the full ACV check to Bardin, then pays the homeowner portion below before the build begins. Any un-performed ACV credit is applied as a deductible credit on the homeowner side, not subtracted here.
              </div>
            </Card>

            <Card
              title="Depreciation Settlement (After Build)"
              dotColor="emerald"
              subtitle="Released by insurance after completion. Includes approved supplements and PWI (paid when invoiced)."
            >
              {calc.acvOnly ? (
                <>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                    <span className="font-semibold">ACV-only claim.</span> Insurance is not paying recoverable depreciation, supplements, or PWI on this deal.
                  </div>
                  <TotalBar label="Depreciation Settlement" value={0} tone="ho" />
                </>
              ) : (
                <>
                  <CalcRow label="Recoverable Depreciation" value={calc.dep} />
                  {calc.depCredit > 0 && (
                    <CalcRow label="− Un-performed DEP" value={-calc.depCredit} indent negative />
                  )}
                  {canSeeSupplements ? (
                    <>
                      <CalcRow label="+ Approved Supplements" value={calc.supplements} indent />
                      {calc.supplementsPending && (
                        <div className="py-1 pl-3 text-[11px] text-amber-700">
                          ⏳ {fmtUSD(calc.supplementInvoiceAmount)} requested from the carrier — not included until approved
                        </div>
                      )}
                    </>
                  ) : calc.supplements > 0 ? (
                    <div className="py-1 pl-3 text-[11px] text-slate-400">+ Supplements (hidden)</div>
                  ) : null}
                  <CalcRow label="+ PWI (paid when invoiced)" value={calc.pwi} indent />
                  <TotalBar label="Depreciation Settlement" value={calc.depSettlement} tone="ho" />
                </>
              )}
              <div className="mt-3 border-t border-slate-100 pt-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Insurance Pays Bardin</div>
                  <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(calc.insurancePays)}</div>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">First payment (insurance portion) + depreciation settlement.</div>
              </div>
            </Card>
          </div>

          {/* HO Out-of-Pocket breakdown */}
          <Card title="Homeowner Out-of-Pocket Breakdown" dotColor="emerald" subtitle="How the HO portion of the first payment is computed.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <div>
                <CalcRow label="Deductible" value={calc.deductible} />
                <CalcRow label="+ Upgrades" value={calc.upgrades} indent />
                <CalcRow label="+ Retail profit" value={calc.retailProfit} indent />
                <CalcRow label="+ @ cost retail" value={calc.atCostRetail} indent />
                <div className="border-t border-slate-100 my-1"></div>
                <CalcRow label="Subtotal" value={calc.hoGross} muted />
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Credits applied to deductible</div>
                <CalcRow label="ACV Credit (un-performed)" value={-calc.acvCredit} indent negative />
                <CalcRow label="Misc Credit" value={-calc.miscCredit} indent negative />
                <CalcRow label="Total applied (capped at ded)" value={-calc.creditsApplied} bold negative />
                <TotalBar label="HO Out-of-Pocket" value={calc.hoOutOfPocket} tone="ho" />
              </div>
            </div>
            {calc.depCredit > 0 && (
              <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
                Un-performed DEP {fmtUSD(calc.depCredit)} only reduces what we collect from insurance — it does not offset the deductible.
              </div>
            )}
            {calc.creditOverage > 0 && (
              <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800">
                ⚠ Credit overage: <span className="font-semibold">{fmtUSD(calc.creditOverage)}</span> not collected.
              </div>
            )}
            {trades.total > 0 && (
              <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
                The trades billed to the homeowner ({fmtUSD(trades.total)}) are due at completion, not in the first payment.
              </div>
            )}
          </Card>

          {/* Commission reconciliation */}
          <div className="bg-slate-900 text-white rounded-xl p-4 mb-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Commission Reconciliation</div>
            <DarkRow label="First Payment (before build)" value={calc.firstPaymentToBardin} />
            <DarkRow label={`+ Depreciation Settlement${calc.acvOnly ? " (ACV only — $0)" : ""}`} value={calc.depSettlement} />
            {trades.total > 0 && (
              <DarkRow label="+ Trades billed to homeowner (due at completion)" value={trades.total} />
            )}
            {calc.creditsApplied > 0 && (
              <DarkRow label="+ Credits applied to deductible (non-cash)" value={calc.creditsApplied} />
            )}
            {calc.acvCredit > 0 && (
              <DarkRow label="− Un-performed ACV (not earned)" value={-calc.acvCredit} rose />
            )}
            <div className="border-t border-slate-700 my-2"></div>
            <DarkRow label="Initial Invoice" value={calc.initialInvoice} bold />
            <DarkRow label="− @ cost retail" value={-calc.atCostRetail} rose />
            {trades.total > 0 && (
              <DarkRow label="− Trades billed to homeowner (floor at cost; upside is its own pool)" value={-trades.total} rose />
            )}
            <DarkRow label="− Misc Credits" value={-calc.miscCredit} rose />
            <DarkRow label="− Non-Collect Revenue" value={-calc.nonCommissionable} rose />
            <DarkRow label="− Punch list RM cost" value={-calc.punchListRmCost} rose />
            <div className="mt-2 flex items-center justify-between px-3 py-2 bg-emerald-500/20 border-2 border-emerald-500 rounded-lg">
              <div className="text-sm font-semibold uppercase tracking-wide">Commissionable Revenue</div>
              <div className="text-lg font-bold">{fmtUSD(calc.commissionable)}</div>
            </div>
            {showTradeSplit && trades.upside > 0 && (
              <div className="mt-2 px-3 py-2 bg-teal-500/15 border border-teal-400/60 rounded-lg">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-teal-200 font-semibold">Trade upside pool (above the 30% floor)</span>
                  <span className="text-teal-100 font-bold">+ {fmtUSD(trades.upside)}</span>
                </div>
                <div className="mt-1 text-[10px] text-teal-200/80">
                  Paid as commission on its own line on the Commission board — the RM by default; Chad can share it with the TL / ASM.
                </div>
              </div>
            )}
            {ncOver > 0 && (
              <div className="mt-2 px-3 py-2 bg-amber-500/15 border border-amber-400/60 rounded-lg">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-amber-200 font-semibold">
                    {trades.marginLoss > 0
                      ? ncDeductActive
                        ? `Non-collect bucket over $500 (non-collect ${fmtUSD(calc.nonCollectRevenue)} + trade shortfalls ${fmtUSD(trades.marginLoss)})`
                        : `Trade shortfalls over $500 (non-collect is grandfathered)`
                      : "Non-collect overage (over $500)"}
                  </span>
                  <span className="text-amber-100 font-bold">− {fmtUSD(ncOver)}</span>
                </div>
                <div className="mt-1 text-[10px] text-amber-200/80">
                  Further deducted from RM commission + TL override at Chad's approval step (75/25 default, Chad-editable per deal).
                </div>
              </div>
            )}
            <div className="mt-2 text-[10px] text-slate-400">
              Non-Collect Revenue and the trade shortfalls Chad deducts share one bucket: the first $500 is forgiven, the rest is deducted from RM + TL commission (75/25 default). Chad can edit the split per deal in the Commission board.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-3 bg-white border-t border-slate-200">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium bg-bardin-teal text-white rounded-lg hover:bg-bardin-teal/90 disabled:opacity-50">
            {saving ? "Saving…" : "Save Scope"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function Card({
  title,
  subtitle,
  tone,
  dotColor,
  children,
}: {
  title: string;
  subtitle?: string;
  tone?: "amber";
  dotColor?: "sky" | "emerald";
  children: React.ReactNode;
}) {
  const toneBorder = tone === "amber" ? "border-amber-200" : "border-slate-200";
  const titleColor = tone === "amber" ? "text-amber-700" : "text-slate-500";
  const dot = dotColor === "sky" ? "bg-sky-500" : dotColor === "emerald" ? "bg-emerald-500" : null;
  return (
    <div className={`bg-white rounded-xl shadow-sm border p-4 mb-4 ${toneBorder}`}>
      <div className="mb-2">
        <div className="flex items-center gap-2">
          {dot && <span className={`w-2 h-2 rounded-full ${dot}`}></span>}
          <div className={`text-[10px] font-semibold uppercase tracking-wider ${titleColor}`}>{title}</div>
        </div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function NumField({ label, value, onChange, hint }: { label: string; value: number; onChange: (v: number) => void; hint?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-bardin-ink">{label}</div>
        {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
      </div>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
        <input
          type="number"
          inputMode="decimal"
          value={value === 0 ? "" : value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="w-28 sm:w-32 pl-7 pr-2 py-1.5 text-right bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-bardin-teal/30"
        />
      </div>
    </div>
  );
}

/**
 * Read-only counterpart to NumField. Used for values whose editor lives
 * elsewhere (e.g., misc_credit which Shannon owns in the Finance Dashboard
 * post-build — May 15, 2026). Renders the dollar amount and a small note
 * pointing at the owning surface.
 */
// Oct 2, 2026 — customer description + internal note under each homeowner
// add-on amount. Spans both grid columns so the notes sit right under their
// amount. Hidden until the add-on has an amount (or an existing note).
function AddOnNoteFields({
  show, description, internalNote, onDescription, onInternalNote, placeholder,
}: {
  show: boolean;
  description: string;
  internalNote: string;
  onDescription: (v: string) => void;
  onInternalNote: (v: string) => void;
  placeholder: string;
}) {
  if (!show) return null;
  return (
    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2 -mt-0.5 pl-3 border-l-2 border-slate-200">
      <label className="block">
        <span className="text-[11px] font-medium text-slate-600">Description — <span className="text-emerald-700">customer sees this when signing</span></span>
        <textarea
          value={description}
          onChange={(e) => onDescription(e.target.value)}
          placeholder={placeholder}
          rows={2}
          maxLength={500}
          className="w-full text-sm p-2 border border-slate-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300"
        />
      </label>
      <label className="block">
        <span className="text-[11px] font-medium text-slate-600">Internal note — <span className="text-violet-700">staff only (build approval + scheduling)</span></span>
        <textarea
          value={internalNote}
          onChange={(e) => onInternalNote(e.target.value)}
          placeholder="Crew / office details the homeowner shouldn't see"
          rows={2}
          maxLength={1000}
          className="w-full text-sm p-2 border border-violet-200 rounded bg-violet-50/40 focus:outline-none focus:ring-2 focus:ring-violet-300"
        />
      </label>
    </div>
  );
}

function ReadOnlyField({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-bardin-ink">{label}</div>
        {note && <div className="text-[10px] text-slate-400">{note}</div>}
      </div>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
        <div
          className="w-28 sm:w-32 pl-7 pr-2 py-1.5 text-right bg-slate-100 border border-slate-200 rounded-md text-sm text-slate-500 cursor-not-allowed select-none"
          aria-readonly="true"
          title="Edited by Shannon in the Finance Dashboard"
        >
          {value === 0 ? "—" : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>
    </div>
  );
}

function CalcRow({ label, value, bold = false, negative = false, indent = false, muted = false }: { label: string; value: number; bold?: boolean; negative?: boolean; indent?: boolean; muted?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-1 ${indent ? "pl-3" : ""}`}>
      <div className={`text-sm ${bold ? "font-semibold text-bardin-ink" : muted ? "text-slate-400" : "text-slate-600"}`}>{label}</div>
      <div className={`text-sm ${bold ? "font-semibold text-bardin-ink" : negative ? "text-rose-600" : muted ? "text-slate-400" : "text-slate-700"}`}>{fmtUSD(value)}</div>
    </div>
  );
}

function TotalBar({ label, value, tone }: { label: string; value: number; tone: "insurance" | "ho" }) {
  const cls = tone === "insurance"
    ? "bg-sky-50 border-sky-300 text-sky-900"
    : "bg-emerald-50 border-emerald-300 text-emerald-900";
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 mt-3 ${cls}`}>
      <div className="text-xs font-semibold uppercase tracking-wide">{label}</div>
      <div className="text-base font-bold">{fmtUSD(value)}</div>
    </div>
  );
}

function DarkRow({ label, value, bold = false, rose = false }: { label: string; value: number; bold?: boolean; rose?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className={`text-sm ${bold ? "font-semibold text-white" : "text-slate-300"}`}>{label}</div>
      <div className={`text-sm ${bold ? "font-semibold text-white" : rose ? "text-rose-300" : "text-slate-100"}`}>{fmtUSD(value)}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agreement Review Modal — submit, approve, request-changes, view signing link
// ---------------------------------------------------------------------------

interface ReviewNote {
  author: string;
  author_id?: string;
  text: string;
  timestamp: string;
}

export function AgreementReviewModal({
  deal,
  open,
  mode = "review",
  role,
  isSuperUser,
  member,
  onClose,
  onReload,
  scopeGate,
}: {
  deal: Deal;
  open: boolean;
  /**
   * 'review' (default) — look at agreement details, take whatever next action
   *   the agreement_status allows (submit, approve, request changes, release).
   * 'previewBeforeGenerate' — preview a freshly-built agreement that hasn't
   *   been written to the DB yet. Footer shows Cancel + Submit for Approval.
   *   Submit performs the atomic generate+pending_approval write.
   */
  mode?: "review" | "previewBeforeGenerate";
  role: string | undefined;
  isSuperUser: boolean;
  member: TeamMember | null;
  onClose: () => void;
  onReload: () => void | Promise<void>;
  /** The scope check the caller already keeps (ScopeActions). Without it the
   *  modal fetches its own (the ASM board, Ops Review). */
  scopeGate?: ScopeGateHook;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [showChangesForm, setShowChangesForm] = useState(false);
  // Release to Robert from the install meeting submits the build (Oct 21, 2026).
  const gate = useBuildSubmitGate();
  // Oct 30, 2026 — the scope check, for the submit gate (RM) and the override
  // panel (Alex and Chad). Insurance agreements only; the caller's copy when it
  // has one, otherwise fetched here while the modal is open.
  const scopeGateApplies =
    deal.is_retail !== true &&
    deal.install_agreement_signed !== true &&
    ["none", "draft", "changes_requested", "pending_approval"].includes((deal.agreement_status as string) ?? "none");
  const ownScope = useScopeGate(!scopeGate && open && scopeGateApplies ? deal.id : null, scopeGateRefreshKey(deal));
  const scope = scopeGate ?? ownScope;
  const [scopeBusy, setScopeBusy] = useState(false);
  // May 2, 2026 bug fix: DealDetail loads `deal_documents` into a separate
  // state variable rather than joining onto the deal object, so checking
  // `deal.deal_documents` here was always undefined — the scope-upload gate
  // would block submission even when the carrier scope was already on file.
  // Query directly so the gate works regardless of how the parent loads docs.
  // null = unknown (still loading), true/false = answered.
  const [hasInsuranceScopeDoc, setHasInsuranceScopeDoc] = useState<boolean | null>(null);

  // Lock body scroll
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Reset form state on close/open
  useEffect(() => {
    if (!open) {
      setReviewNote("");
      setShowChangesForm(false);
      setError(null);
    }
  }, [open]);

  // Query for an insurance_scope doc whenever the modal opens or the deal
  // changes. Uses HEAD + count:exact so we don't pull rows we don't need.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setHasInsuranceScopeDoc(null);
    (async () => {
      const { count, error: qErr } = await supabase
        .from("deal_documents")
        .select("id", { head: true, count: "exact" })
        .eq("deal_id", deal.id)
        .eq("file_type", "insurance_scope");
      if (cancelled) return;
      if (qErr) {
        // Network/RLS hiccup — assume not present so the gate stays safe.
        setHasInsuranceScopeDoc(false);
        return;
      }
      setHasInsuranceScopeDoc((count ?? 0) > 0);
    })();
    return () => { cancelled = true; };
  }, [open, deal.id]);

  // Jun 17, 2026 — non-collect overage split gate. When non-collect revenue is
  // over $500, the approver (ASM) must record how the overage is split across
  // RM / TL / ASM before the agreement can be approved (hard gate). Persisted to
  // deals.non_collect_{rm,tl,asm}_amount and surfaced on the Commission board.
  const [ncMembers, setNcMembers] = useState<Record<string, { full_name: string; team_lead_id: string | null; asm_id: string | null }>>({});
  const [ncRm, setNcRm] = useState("");
  const [ncTl, setNcTl] = useState("");
  const [ncAsm, setNcAsm] = useState("");
  const [ncInit, setNcInit] = useState(false);

  // Load active roster (so we can resolve the owner's TL + ASM and their names).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("team_roster")
        .select("id, full_name, team_lead_id, asm_id")
        .eq("is_active", true);
      if (cancelled || !data) return;
      const map: Record<string, { full_name: string; team_lead_id: string | null; asm_id: string | null }> = {};
      data.forEach((r) => { map[r.id] = { full_name: r.full_name, team_lead_id: r.team_lead_id, asm_id: r.asm_id }; });
      setNcMembers(map);
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Reset the one-time prefill guard when the modal closes.
  useEffect(() => { if (!open) setNcInit(false); }, [open]);

  // One-time prefill of the split inputs when the gate is in play: existing
  // saved amounts if present, else the full overage on the RM as a starting point.
  // Oct 29, 2026: the overage is the deal's blended bucket (calcScope
  // nonCollectOverage = non-collect + trade shortfalls − $500), same as below.
  useEffect(() => {
    if (!open || ncInit) return;
    const overage = deal.is_retail === true ? 0 : calcScope(deal).nonCollectOverage;
    if (overage <= 0) return;
    if (deal.owner_id && Object.keys(ncMembers).length === 0) return; // wait for roster
    const exRm = deal.non_collect_rm_amount;
    const exTl = deal.non_collect_tl_amount;
    const exAsm = deal.non_collect_asm_amount;
    const hasExplicit = [exRm, exTl, exAsm].some((v) => v !== null && v !== undefined);
    if (hasExplicit) {
      setNcRm(Number(exRm ?? 0).toFixed(2));
      setNcTl(Number(exTl ?? 0).toFixed(2));
      setNcAsm(Number(exAsm ?? 0).toFixed(2));
    } else {
      setNcRm(overage.toFixed(2));
      setNcTl("0.00");
      setNcAsm("0.00");
    }
    setNcInit(true);
  }, [open, ncInit, ncMembers, deal]);

  if (!open) return null;

  const status = (deal.agreement_status as string) ?? "none";
  const signed = deal.install_agreement_signed === true;
  const canSeeSupplements = isSuperUser || role === "admin" || role === "sales_director" || role === "finance";
  const calc = calcScope(canSeeSupplements ? deal : maskSupplementsForField(deal));

  // Permissions
  const isChadTier = isSuperUser || role === "admin" || role === "sales_director" || role === "finance";
  // Jun 16, 2026 — install-agreement INITIAL approval moved from Chad to the
  // ASM (Alex). The ASM can Approve / Request Changes at the first gate
  // (pending_approval). Chad + super-users keep it too (backup coverage). The
  // SECOND gate (Release to Robert, ready_for_dispatch) stays Chad/super-only —
  // see canRelease below.
  const isAsmApprover = role === "asm";
  const isRmTier = role === "asm" || role === "team_lead" || role === "roof_team_manager" || role === "restore_manager" || isChadTier;

  const isPreviewMode = mode === "previewBeforeGenerate";
  // Hard gate (Apr 30, 2026): the install agreement cannot be submitted to
  // Chad without an insurance scope document on file. Three signals all count:
  //   1. legacy `scope_document_url` column populated, or
  //   2. `deals.insurance_paperwork_uploaded` flipped to true (set by the
  //      scope editor's upload step + by the assign-RS transition), or
  //   3. a `deal_documents` row of file_type='insurance_scope' exists.
  //
  // May 2, 2026 fix: the deal_documents check used to read a (never-populated)
  // joined relation off the deal object — DealDetail loads docs into separate
  // state and never attached them. Now it's a live query (useEffect above).
  // While the count is still loading (null), treat the scope as present for UI
  // purposes — we'd rather flicker the warning briefly than show it incorrectly.
  // Submit handlers re-query at click time so a stale "true" can't sneak through.
  const scopeUploaded =
    deal.is_retail === true || // retail jobs have no carrier scope document
    !!deal.scope_document_url ||
    !!deal.insurance_paperwork_uploaded ||
    hasInsuranceScopeDoc !== false;
  // RM can submit a brand-new agreement (preview mode) OR push an existing
  // legacy 'draft' through to Chad. New flow never produces a 'draft'.
  const canSubmit = isRmTier && (isPreviewMode || status === "draft") && scopeUploaded;
  // First gate — ASM (primary) + Chad/super (backup) approve the agreement.
  const canApprove = (isChadTier || isAsmApprover) && status === "pending_approval";
  // Stage-aware (Oct 23, 2026): once the build is submitted or approved the
  // board owns it; the second gate here closes, whatever the status says.
  const modalBuildState = buildSubmitState(deal.stage);
  const readyToSubmit = status === "ready_for_dispatch" && modalBuildState === "before";
  const canRequestChanges =
    (isChadTier && (status === "pending_approval" || readyToSubmit)) ||
    (isAsmApprover && status === "pending_approval");
  // Second gate — release to Robert after sign + colors + preferred date.
  // Stays Chad/super-only; the ASM does not handle dispatch release.
  const canRelease = isChadTier && readyToSubmit;

  // Scope gate (Oct 30, 2026): while it blocks, the RM's Submit turns into
  // "Can't submit yet" (with an override request when Alex or Chad may give
  // one) and Approve stays off until Alex or Chad use the carrier's numbers,
  // override with a reason, or send it back. The trigger decides either way.
  const scopeStatus = scopeGateApplies ? scope.status : null;
  const scopeBlocks = !!scopeStatus?.applies && !!scopeStatus?.blocks;
  const scopeChecking = scopeGateApplies && !scope.status && scope.loading;
  const submitBlockedByScope = canSubmit && scopeBlocks;
  const approveBlockedByScope = canApprove && scopeBlocks;
  const showApproverScopePanel = canApprove && !!scopeStatus && (scopeBlocks || !!scopeStatus.gate?.request);
  const rmName = deal.owner_id ? ncMembers[deal.owner_id]?.full_name ?? null : null;
  const modalPicks = calc.isRetail ? [] : dealPicks(deal);

  // Non-collect overage split gate (Jun 17, 2026). Active only at the approval
  // step when the deal has non-collect revenue over $500.
  // Oct 29, 2026 (D3): the deal's ONE bucket — non-collect revenue + the trade
  // shortfalls Chad deducted, first $500 forgiven (calcScope nonCollectOverage).
  // The split recorded here is the split of that bucket; the Commission board
  // re-splits any shortfall Chad deducts later on top of it. Retail stays off
  // the gate (a retail shortfall comes off the RM on the board).
  const trades = tradeMoneyOf(calc);
  const showTradeSplit = seesTradeSplit(member);
  const ncOverage = deal.is_retail === true ? 0 : calc.nonCollectOverage;
  const ncOwnerRow = deal.owner_id ? ncMembers[deal.owner_id] : undefined;
  const ncTlId = ncOwnerRow?.team_lead_id ?? null;
  const ncAsmId = ncOwnerRow?.asm_id ?? null;
  const ncHasTl = !!(ncTlId && ncMembers[ncTlId]);
  const ncHasAsm = !!(ncAsmId && ncMembers[ncAsmId]);
  const ncTlName = ncTlId ? ncMembers[ncTlId]?.full_name ?? "TL" : null;
  const ncAsmName = ncAsmId ? ncMembers[ncAsmId]?.full_name ?? "ASM" : null;
  const ncRmN = Math.max(0, Number(ncRm) || 0);
  const ncTlN = ncHasTl ? Math.max(0, Number(ncTl) || 0) : 0;
  const ncAsmN = ncHasAsm ? Math.max(0, Number(ncAsm) || 0) : 0;
  const ncAllocated = ncRmN + ncTlN + ncAsmN;
  const ncRemaining = ncOverage - ncAllocated;
  const ncBalanced = Math.abs(ncRemaining) < 0.005;
  // Gate fires whenever the approver is approving a deal with overage > 0.
  const ncGateActive = canApprove && ncOverage > 0;

  const reviewNotes = (deal.agreement_review_notes as unknown as ReviewNote[]) ?? [];
  const signingUrl = (deal.signing_link || deal.agreement_signing_url) as string | null;
  const pdfUrl = deal.install_agreement_pdf_url;

  function buildAgreementData() {
    // Delegates to the module-scope builder (single source of truth for the
    // install-agreement payload — also used by ScopeActions' resubmit flow).
    return buildAgreementPayload(deal);
  }

  // Defensive re-query at click time. Prevents a stale "scope present" read
  // from the loading-state optimistic gate from sneaking through, and catches
  // the rare race where the doc is deleted between modal open and click.
  async function verifyScopeOnFile(): Promise<boolean> {
    if (deal.is_retail === true) return true; // no carrier scope on retail jobs
    if (deal.scope_document_url) return true;
    if (deal.insurance_paperwork_uploaded) return true;
    const { count } = await supabase
      .from("deal_documents")
      .select("id", { head: true, count: "exact" })
      .eq("deal_id", deal.id)
      .eq("file_type", "insurance_scope");
    return (count ?? 0) > 0;
  }

  // The trigger's refusal (scope gate) is shown as it is, and the check is
  // fetched again; anything else is thrown.
  function scopeRefused(err: unknown): boolean {
    const refusal = scopeGateRefusal(err);
    if (!refusal) return false;
    setError(refusal);
    void scope.refresh();
    return true;
  }

  async function handleGenerateAndSubmit(overrideRequest?: string) {
    // Preview-mode submit: write agreement_data + jump straight to
    // pending_approval (skipping the legacy 'draft' state). The RM has
    // already previewed the numbers — Chad gets it next.
    setSubmitting(true);
    setError(null);
    try {
      if (!(await verifyScopeOnFile())) throw new Error("Insurance scope must be uploaded before submitting for approval. Add the carrier's scope to the Documents section first.");
      const agreementData = buildAgreementData();
      const { error: upErr } = await supabase.from("deals").update({
        agreement_data: agreementData,
        agreement_status: "pending_approval",
        agreement_generated_date: new Date().toISOString(),
        // Oct 30, 2026: a submission the scope check blocks goes as a request for Alex or Chad's override.
        ...(overrideRequest ? { scope_gate: { request: { note: overrideRequest } } } : {}),
      }).eq("id", deal.id);
      if (upErr) {
        if (scopeRefused(upErr)) return;
        throw upErr;
      }
      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "agreement",
        content: "Install agreement generated and submitted for Chad's approval."
          + (overrideRequest ? ` Asked Alex or Chad to override the scope check: ${overrideRequest}` : ""),
        metadata: { action: "generate_and_submit_for_approval", ...(overrideRequest ? { scope_override_requested: true } : {}) },
      });
      // Fire-and-forget: ping Chad on phone + in-app notification.
      const { notifyChadOnAgreementSubmitted } = await import("../../lib/notifyChadOnAgreementSubmitted");
      void notifyChadOnAgreementSubmitted(deal, { overrideRequest: overrideRequest ?? null });
      await onReload();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitForApproval(overrideRequest?: string) {
    setSubmitting(true);
    setError(null);
    try {
      if (!(await verifyScopeOnFile())) throw new Error("Insurance scope must be uploaded before submitting for approval. Add the carrier's scope to the Documents section first.");
      const { error: upErr } = await supabase.from("deals").update({
        agreement_status: "pending_approval",
        ...(overrideRequest ? { scope_gate: { request: { note: overrideRequest } } } : {}),
      }).eq("id", deal.id);
      if (upErr) {
        if (scopeRefused(upErr)) return;
        throw upErr;
      }
      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "agreement",
        content: "Install agreement submitted for Chad's approval."
          + (overrideRequest ? ` Asked Alex or Chad to override the scope check: ${overrideRequest}` : ""),
        metadata: { action: "submit_for_approval", ...(overrideRequest ? { scope_override_requested: true } : {}) },
      });
      // Same Chad ping as the new generate-and-submit path (legacy draft flow).
      const { notifyChadOnAgreementSubmitted } = await import("../../lib/notifyChadOnAgreementSubmitted");
      void notifyChadOnAgreementSubmitted(deal, { overrideRequest: overrideRequest ?? null });
      await onReload();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  // Alex or Chad, while the scope check blocks: the carrier's numbers (with the
  // caches and a fresh agreement snapshot) and the approval in ONE update, so the
  // trigger sees the matching numbers when it judges the approval.
  async function handleApproveWithCarrierNumbers() {
    if (!scopeStatus) return;
    const numbers = carrierNumbersPatch(scopeStatus.fields);
    setScopeBusy(true);
    try {
      // The snapshot and the caches come from the deal as it is now, not the page's copy of it.
      let current: Deal;
      try {
        current = await fetchDealForScope(deal.id);
      } catch (e) {
        setError((e as Error).message);
        return;
      }
      const next = { ...current, ...numbers } as Deal;
      await handleApprove({
        extra: { ...numbers, ...scopeCachePatch(next), agreement_data: buildAgreementPayload(next) },
        noteSuffix: ` Used the carrier's numbers first: ${describeNumberChanges(scopeStatus.fields)}.`,
        metadata: { scope_carrier_numbers: numbers },
      });
    } finally {
      setScopeBusy(false);
    }
  }

  // Override with a reason: the record goes back with the RM's request as it is
  // (the trigger refuses a request changed at approval) plus the override.
  async function handleOverrideAndApprove(reason: string) {
    const record = scopeStatus?.gate ?? deal.scope_gate ?? {};
    setScopeBusy(true);
    try {
      await handleApprove({
        extra: { scope_gate: { ...record, override: { reason } } },
        noteSuffix: ` Scope check overridden: ${reason}`,
        metadata: { scope_override: reason },
      });
    } finally {
      setScopeBusy(false);
    }
  }

  function handleSendBackForScope(note: string) {
    setReviewNote(note);
    setShowChangesForm(true);
  }

  async function handleApprove(opts?: { extra?: Record<string, unknown>; noteSuffix?: string; metadata?: Record<string, unknown> }) {
    // Hard gate (Jun 17, 2026): non-collect overage must be split across
    // RM / TL / ASM and total the overage before approval.
    if (ncGateActive && !ncBalanced) {
      setError(`Record the Non-Collect overage split first — RM + TL + ASM must total ${fmtUSD(ncOverage)} (currently ${fmtUSD(ncAllocated)}).`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Generate the signing link client-side. Token format must match
      // SigningPage / useHomeownerAuth: base64(dealId:lastName:streetNum).
      // We do this client-side because the legacy generate-agreement edge
      // function depends on a parsed_scopes row, which doesn't exist for
      // deals scoped through the new ScopeEditorModal flow (scope-parser
      // was disabled Apr 24, 2026).
      const nameParts = (deal.homeowner_name || "").trim().split(/\s+/);
      const lastName = nameParts[nameParts.length - 1] || "UNKNOWN";
      const streetNum = (deal.property_street || "").match(/^(\d+)/)?.[1] ?? "";
      const token = btoa(`${deal.id}:${lastName}:${streetNum}`);
      const signingUrl = `https://portal.bardinroofing.com/sign?token=${token}`;

      if (!streetNum) {
        // Token decoding REQUIRES three colon-separated fields. Empty street
        // number breaks the homeowner login lookup downstream.
        throw new Error("Property street is missing a leading number — fix the address before approving so the homeowner can sign.");
      }

      const { error: upErr } = await supabase.from("deals").update({
        agreement_status: "approved",
        agreement_last_reviewed_by: member?.id,
        agreement_last_reviewed_at: new Date().toISOString(),
        agreement_revision_count: (deal.agreement_revision_count ?? 0) + 1,
        scope_confirmed: true,
        signing_link: signingUrl,
        // Persist the recorded non-collect overage split + activate the
        // deduction on this deal (Jun 17, 2026).
        ...(ncOverage > 0 ? {
          non_collect_deduct_active: true,
          non_collect_rm_amount: ncRmN,
          non_collect_tl_amount: ncHasTl ? ncTlN : 0,
          non_collect_asm_amount: ncHasAsm ? ncAsmN : 0,
        } : {}),
        // Oct 30, 2026: the carrier's numbers or Alex/Chad's override ride in the same update.
        ...(opts?.extra ?? {}),
      }).eq("id", deal.id);
      if (upErr) {
        if (scopeRefused(upErr)) return;
        throw upErr;
      }

      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "agreement",
        content: `Install agreement approved by ${member?.full_name ?? "admin"}. Signing link generated.${opts?.noteSuffix ?? ""}`,
        metadata: { action: "approved", signing_url: signingUrl, ...(opts?.metadata ?? {}) },
      });

      // No QuickBooks customer from here any more (Oct 22, 2026): the
      // quickbooks-sync call never reached the function (the browser stopped
      // it at the CORS preflight), and Bardin leaves QuickBooks for Books.

      await onReload();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRequestChanges() {
    if (!reviewNote.trim()) {
      setError("Please add a note explaining what needs to change.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const newNote: ReviewNote = {
        author: member?.full_name ?? "Admin",
        author_id: member?.id ?? "",
        text: reviewNote.trim(),
        timestamp: new Date().toISOString(),
      };
      const { error: upErr } = await supabase.from("deals").update({
        agreement_status: "changes_requested",
        agreement_last_reviewed_by: member?.id,
        agreement_last_reviewed_at: new Date().toISOString(),
        agreement_review_notes: [...reviewNotes, newNote],
      }).eq("id", deal.id);
      if (upErr) throw upErr;
      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "agreement",
        content: `Changes requested: ${reviewNote.trim()}`,
        metadata: { action: "changes_requested" },
      });
      setReviewNote("");
      setShowChangesForm(false);
      await onReload();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReleaseToRobert() {
    // Chad's second approval of the AGREEMENT: flips agreement_status to
    // 'released'. One route (Oct 22, 2026): a deal at install_meeting is also
    // SUBMITTED for its build here (stage → build_submitted) and lands on the
    // Build Approvals board, where Chad or Robert approves it. It no longer
    // jumps to schedule_build. At any other stage only the status flips.
    setSubmitting(true);
    setError(null);
    try {
      const willAdvanceStage = deal.stage === "install_meeting";
      const updates: Record<string, unknown> = {
        agreement_status: "released",
        agreement_last_reviewed_by: member?.id,
        agreement_last_reviewed_at: new Date().toISOString(),
        agreement_revision_count: (deal.agreement_revision_count ?? 0) + 1,
      };
      if (willAdvanceStage) {
        updates.stage = "build_submitted";
        updates.days_in_stage = 0;
      }
      // Moving from the install meeting submits the build: the first payment
      // plan is asked here when it isn't captured (Oct 21, 2026).
      const pass = await gate.run(
        {
          dealId: deal.id, homeowner: deal.homeowner_name, fromStage: deal.stage,
          toStage: willAdvanceStage ? "build_submitted" : String(deal.stage ?? ""),
          source: "release_to_robert", title: "Submit build", actionLabel: "Submit build",
        },
        async () => {
          const { error: upErr } = await supabase.from("deals").update(updates).eq("id", deal.id);
          if (upErr) throw upErr;
        },
      );
      if (!pass) return; // cancelled
      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "agreement",
        content: willAdvanceStage
          ? `Build submitted by ${member?.full_name ?? "admin"} (agreement released). Waiting for approval on the Build Approvals board.`
          : `Agreement released by ${member?.full_name ?? "admin"}. Stage left at ${deal.stage}.`,
        metadata: {
          action: "released_to_robert", advanced_stage: willAdvanceStage, prior_stage: deal.stage,
          ...(pass.plan ? { payment_plan: pass.plan, payment_plan_note: pass.note } : {}),
        },
      });

      // The deal just landed on the Build Approvals board: Chad is texted and
      // Robert gets the board note, like every other submission path. Nothing
      // goes to the person who submitted it.
      if (willAdvanceStage) {
        void notifyChadOnBuildApproval(deal, { actorId: member?.id, plan: passToNotifyPlan(pass) });
      }

      await onReload();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm">
      {gate.element}
      <div className="bg-slate-50 w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[92vh] sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-500">{isPreviewMode ? "Preview Install Agreement" : "Install Agreement"}</div>
            <div className="text-base font-semibold text-bardin-ink flex items-center gap-2">
              {deal.homeowner_name ?? "Agreement Review"}
              {!isPreviewMode && <AgreementStatusChip status={status} signed={signed} stage={deal.stage} />}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-slate-500 hover:text-bardin-ink rounded-lg hover:bg-slate-100"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
          {/* Preview-mode banner */}
          {isPreviewMode && (
            <div className="bg-slate-900 text-white rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Pre-submission preview</div>
              <div className="text-sm">
                Review the numbers below. Hit <span className="font-semibold">Submit for Approval</span> to send to Chad,
                or <span className="font-semibold">Cancel</span> to go back and tweak the scope.
              </div>
            </div>
          )}

          {/* Build already submitted or approved: the board owns it now */}
          {!isPreviewMode && (status === "ready_for_dispatch" || status === "released") && modalBuildState !== "before" && (
            <div className={`rounded-xl p-4 border ${modalBuildState === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
              <div className="text-xs font-semibold uppercase tracking-wider mb-1">
                {modalBuildState === "approved" ? "Build approved" : "Build submitted"}
              </div>
              <div className="text-sm">
                {modalBuildState === "approved"
                  ? "Approved on the Build Approvals board. Robert schedules it from there."
                  : `Waiting for approval on the ${BUILD_APPROVAL_BOARD}.`}
              </div>
            </div>
          )}

          {/* Ready-for-dispatch banner — Chad's release context */}
          {!isPreviewMode && readyToSubmit && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-purple-900 uppercase tracking-wider mb-1">Ready to submit the build</div>
              <div className="text-sm text-purple-900">
                HO signed, colors chosen, preferred build date set
                {deal.ho_preferred_build_date ? ` to ${new Date((deal.ho_preferred_build_date as string) + "T12:00:00").toLocaleDateString()}` : ""}
                . Submit build sends it to the Build Approvals board, where Chad or Robert approves it; Robert schedules it from there.
              </div>
              {deal.stage !== "install_meeting" && (
                <div className="mt-2 text-xs text-purple-700">
                  Stage is currently <span className="font-medium">{deal.stage}</span>. Only the agreement status
                  changes here; the stage is moved only from the install meeting.
                </div>
              )}
            </div>
          )}

          {/* Deal meta */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-slate-500">Property</div>
                <div className="text-bardin-ink">
                  {isAddressLinkable(deal) ? (
                    <a
                      href={mapsHref(deal)}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:underline hover:text-slate-700"
                    >
                      {[deal.property_street, deal.property_city].filter(Boolean).join(", ")}
                    </a>
                  ) : (
                    [deal.property_street, deal.property_city].filter(Boolean).join(", ") || "—"
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Insurance</div>
                <div className="text-bardin-ink">{deal.insurance_company || "—"}{deal.claim_number ? ` · #${deal.claim_number}` : ""}</div>
              </div>
            </div>
          </div>

          {/* Scope numbers summary */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
              <span>Scope Summary</span>
              {calc.isRetail && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-teal-100 text-teal-800 normal-case tracking-normal">🏷 Retail · 50/50</span>
              )}
              {!calc.isRetail && calc.acvOnly && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800 normal-case tracking-normal">ACV only</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <StatTile label={calc.isRetail ? "50% Deposit (Before Build)" : "First Payment (Pre-Build)"} value={calc.firstPaymentToBardin} tone="insurance" />
              <StatTile label={calc.isRetail ? (trades.total > 0 ? "Due on Completion (50% + trades)" : "50% Due on Completion") : calc.acvOnly ? "Dep Settlement (ACV only)" : "Depreciation Settlement"} value={calc.depSettlement} tone="ho" />
            </div>
            <div className="flex items-center justify-between py-1 border-t border-slate-100">
              <div className="text-sm text-slate-600">{calc.isRetail ? (trades.total > 0 ? "Contract + trades" : "Contract Total") : "Initial Invoice"}</div>
              <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(calc.initialInvoice)}</div>
            </div>
            {/* Oct 29, 2026 — the trade lines on the agreement being approved. */}
            <TradesBilledRows trades={trades} showSplit={showTradeSplit} signed={signed} bordered={false} />
            <div className="flex items-center justify-between py-1">
              <div className="text-sm text-slate-600">{calc.isRetail ? "Commission Pool (over base)" : "Commissionable Revenue"}</div>
              <div className="text-sm font-semibold text-emerald-700">{fmtUSD(calc.commissionable)}</div>
            </div>
            {calc.isRetail && deal.retail_scope_description && (
              <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
                <div className="font-semibold mb-0.5">Scope of work</div>
                <div>{deal.retail_scope_description}</div>
              </div>
            )}
            {modalPicks.length > 0 ? (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                <div className="font-semibold mb-1">Work Bardin won't do ({modalPicks.length} carrier {modalPicks.length === 1 ? "line" : "lines"})</div>
                <div className="rounded bg-white border border-amber-100 p-2">
                  <PickedLinesList picks={modalPicks} />
                </div>
                <div className="mt-1">
                  ACV <span className="font-medium">{fmtUSD(calc.acvCredit)}</span> credited toward HO deductible; DEP <span className="font-medium">{fmtUSD(calc.depCredit)}</span> not collected from insurance.
                </div>
              </div>
            ) : deal.items_not_performing_lines ? (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                <div className="font-semibold mb-0.5">Un-performed line items</div>
                <div>{deal.items_not_performing_lines}</div>
                <div className="mt-1">
                  ACV <span className="font-medium">{fmtUSD(calc.acvCredit)}</span> credited toward HO deductible; DEP <span className="font-medium">{fmtUSD(calc.depCredit)}</span> not collected from insurance.
                </div>
              </div>
            ) : null}
            {!calc.isRetail && <AddOnNotesCard deal={deal} className="mt-2" />}
            {calc.creditOverage > 0 && (
              <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800">
                ⚠ Credit overage: {fmtUSD(calc.creditOverage)} not collected
              </div>
            )}
          </div>

          {/* Review notes history */}
          {reviewNotes.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Review Notes</div>
              <ul className="space-y-2">
                {reviewNotes.map((note, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-xs flex-shrink-0">💬</div>
                    <div>
                      <div className="text-bardin-ink">{note.text}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {note.author} · {new Date(note.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Signing link when approved/sent */}
          {(status === "approved" || status === "sent") && signingUrl && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Signing Link</div>
              <a
                href={signingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-bardin-teal hover:underline break-all"
              >
                {signingUrl}
              </a>
            </div>
          )}

          {/* PDF link when signed */}
          {signed && pdfUrl && (
            <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-4">
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2">
                Signed {deal.install_agreement_signed_date ? `on ${new Date(deal.install_agreement_signed_date as string).toLocaleDateString()}` : ""}
              </div>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-bardin-teal hover:underline"
              >
                View Signed PDF →
              </a>
            </div>
          )}
          {signed && !pdfUrl && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2">
                Signed {deal.install_agreement_signed_date ? `on ${new Date(deal.install_agreement_signed_date as string).toLocaleDateString()}` : ""}
              </div>
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                ⚠ PDF couldn't be generated automatically. The signature is on file (legal record), but the formatted PDF is missing. Click below to regenerate.
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data: fullDeal, error: fetchErr } = await supabase
                      .from("deals").select("*").eq("id", deal.id).single();
                    if (fetchErr || !fullDeal) throw fetchErr ?? new Error("deal lookup failed");
                    const { generateInstallAgreementPDF } = await import("../../lib/installAgreementPDF");
                    // Pull signature from the activity_log if the deal column doesn't carry it.
                    const { data: signLogs } = await supabase
                      .from("activity_log")
                      .select("metadata")
                      .eq("deal_id", deal.id)
                      .filter("metadata->>field", "eq", "agreement_status")
                      .order("performed_at", { ascending: false })
                      .limit(1);
                    const sigUrlFromLog = (signLogs?.[0]?.metadata as { signature_url?: string } | null)?.signature_url ?? null;
                    // Retail deals always number RETAIL-LASTNAME even when a
                    // (denied) claim number is still on the deal — matches the
                    // number SigningPage stamps at sign time. Jun 11, 2026.
                    const claim = fullDeal.is_retail === true ? "RETAIL" : fullDeal.claim_number || "RETAIL";
                    const lastName = (fullDeal.homeowner_name || "").split(" ").pop()?.toUpperCase() || "";
                    const agreementNumber = `${claim}-${lastName}`;
                    const pdfBlob = await generateInstallAgreementPDF(fullDeal, sigUrlFromLog, agreementNumber);
                    const pdfPath = `install-agreements/${deal.id}/${Date.now()}-signed.pdf`;
                    const { error: upErr } = await supabase.storage
                      .from("deal-documents")
                      .upload(pdfPath, pdfBlob, { contentType: "application/pdf", upsert: false });
                    if (upErr) throw upErr;
                    const { data: signed, error: signErr } = await supabase.storage
                      .from("deal-documents")
                      .createSignedUrl(pdfPath, 31536000);
                    if (signErr || !signed?.signedUrl) throw signErr ?? new Error("createSignedUrl null");
                    await supabase.from("deals").update({
                      install_agreement_pdf_url: signed.signedUrl,
                    }).eq("id", deal.id);
                    await supabase.from("deal_documents").insert({
                      deal_id: deal.id,
                      file_type: "agreement",
                      storage_path: pdfPath,
                      file_name: `${agreementNumber}-signed.pdf`,
                    });
                    window.location.reload();
                  } catch (e) {
                    alert(`PDF regeneration failed: ${(e as Error)?.message ?? "unknown error"}`);
                  }
                }}
                className="text-xs font-semibold bg-bardin-orange text-white px-3 py-1.5 rounded hover:bg-bardin-orange/90"
              >
                Regenerate PDF
              </button>
            </div>
          )}

          {/* Scope gate (Oct 30, 2026): Alex or Chad decide a deal that doesn't match the carrier's scope. */}
          {showApproverScopePanel && scopeStatus && (
            <ScopeGateApproverPanel
              deal={deal}
              status={scopeStatus}
              rmName={rmName}
              canOverride={canOverrideScope(role, isSuperUser)}
              busy={submitting || scopeBusy}
              onApproveWithCarrierNumbers={handleApproveWithCarrierNumbers}
              onOverride={handleOverrideAndApprove}
              onSendBack={handleSendBackForScope}
              showPicks={modalPicks.length === 0}
            />
          )}
          {canApprove && scopeChecking && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">Checking the deal against the carrier's scope…</div>
          )}

          {/* Request-changes form */}
          {canRequestChanges && showChangesForm && (
            <div className="bg-white rounded-xl shadow-sm border border-rose-200 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-rose-700 mb-2">What needs to change?</div>
              <textarea
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder="Explain the changes needed (e.g., missing ridge vent upgrade, wrong deductible amount)"
                className="w-full text-sm p-2 border border-slate-200 rounded bg-slate-50 focus:outline-none focus:ring-2 focus:ring-rose-300"
                rows={3}
                autoFocus
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => { setShowChangesForm(false); setReviewNote(""); }}
                  disabled={submitting}
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestChanges}
                  disabled={submitting || !reviewNote.trim()}
                  className="px-3 py-1.5 text-xs font-medium bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Send Changes Request"}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        {/* Scope-upload gate banner: visible when RM is in submit-eligible
            state but the carrier scope isn't uploaded yet. Apr 30, 2026. */}
        {isRmTier && (isPreviewMode || status === "draft") && !scopeUploaded && (
          <div className="mx-4 sm:mx-6 mt-3 mb-1 rounded-lg bg-amber-50 border border-amber-300 p-3">
            <div className="flex items-start gap-2">
              <span className="text-base leading-none">⚠</span>
              <div className="text-xs leading-snug">
                <div className="font-semibold text-amber-900 mb-0.5">Upload the insurance scope first</div>
                <div className="text-amber-800">
                  The install agreement can't be submitted to Chad until the carrier's scope is on file. Add it to the Documents section on this deal, then come back here.
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Scope gate (Oct 30, 2026): Submit stays off while the deal doesn't match the carrier's scope. */}
        {submitBlockedByScope && scopeStatus && (
          <div className="mx-4 sm:mx-6 mt-3 mb-1">
            <ScopeSubmitBlock
              status={scopeStatus}
              busy={submitting}
              fixHint={isPreviewMode ? "Close this preview and fix it on the scope check." : "Close this and fix it on the scope check on the deal page."}
              onRequest={(note) => (isPreviewMode ? handleGenerateAndSubmit(note) : handleSubmitForApproval(note))}
            />
          </div>
        )}
        {/* Non-collect overage split gate (Jun 17, 2026). The approver must
            record how the over-$500 overage is split across RM / TL / ASM
            before the agreement can be approved. */}
        {ncGateActive && (
          <div className="mx-4 sm:mx-6 mt-3 mb-1 rounded-lg bg-amber-50 border border-amber-300 p-3">
            <div className="text-sm font-semibold text-amber-900 mb-0.5">
              📋 Record the Non-Collect overage split — required to approve
            </div>
            <div className="text-xs text-amber-800 mb-2">
              {trades.marginLoss > 0 ? (
                <>
                  Non-collect {fmtUSD(Number(deal.non_commissionable) || 0)} + trade shortfalls {fmtUSD(trades.marginLoss)} share one
                  bucket; the first $500 is forgiven. Enter how the <span className="font-semibold">{fmtUSD(ncOverage)}</span> over
                  that is deducted across RM / TL / ASM. The amounts must total it.
                </>
              ) : (
                <>
                  Non-Collect Revenue is {fmtUSD(Number(deal.non_commissionable) || 0)}. Enter how the <span className="font-semibold">{fmtUSD(ncOverage)}</span> overage is deducted across RM / TL / ASM. The amounts must total the overage.
                </>
              )}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <label className="text-xs text-amber-900 font-medium w-32 shrink-0">RM</label>
                <span className="text-amber-800 text-xs">$</span>
                <input type="number" min={0} step={0.01} value={ncRm} disabled={submitting}
                  onChange={(e) => setNcRm(e.target.value)}
                  className="w-28 text-xs text-right border border-amber-300 rounded px-2 py-1 bg-white" />
              </div>
              {ncHasTl && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-amber-900 font-medium w-32 shrink-0">TL ({ncTlName})</label>
                  <span className="text-amber-800 text-xs">$</span>
                  <input type="number" min={0} step={0.01} value={ncTl} disabled={submitting}
                    onChange={(e) => setNcTl(e.target.value)}
                    className="w-28 text-xs text-right border border-amber-300 rounded px-2 py-1 bg-white" />
                </div>
              )}
              {ncHasAsm && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-amber-900 font-medium w-32 shrink-0">ASM ({ncAsmName})</label>
                  <span className="text-amber-800 text-xs">$</span>
                  <input type="number" min={0} step={0.01} value={ncAsm} disabled={submitting}
                    onChange={(e) => setNcAsm(e.target.value)}
                    className="w-28 text-xs text-right border border-amber-300 rounded px-2 py-1 bg-white" />
                </div>
              )}
              {!ncHasTl && !ncHasAsm && (
                <div className="text-[11px] text-amber-700 italic">No TL/ASM on this deal — full overage on the RM.</div>
              )}
            </div>
            <div className={`text-[11px] font-medium mt-2 ${ncBalanced ? "text-emerald-700" : "text-red-700"}`}>
              Allocated {fmtUSD(ncAllocated)} of {fmtUSD(ncOverage)} · {ncBalanced ? "balanced ✓ — ready to approve" : `${ncRemaining > 0 ? "under" : "over"} by ${fmtUSD(Math.abs(ncRemaining))}`}
            </div>
            {(ncHasTl || ncHasAsm) && (
              <button
                onClick={() => { setNcRm(ncOverage.toFixed(2)); setNcTl("0.00"); setNcAsm("0.00"); }}
                disabled={submitting}
                className="mt-1 px-2 py-1 text-[11px] font-medium rounded bg-amber-100 text-amber-900 hover:bg-amber-200 disabled:opacity-50"
                title="Put the full overage on the RM"
              >
                All to RM
              </button>
            )}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-3 bg-white border-t border-slate-200 flex-wrap">
          <button onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
            {isPreviewMode ? "Cancel" : "Close"}
          </button>
          {canSubmit && (
            <button
              onClick={() => (isPreviewMode ? handleGenerateAndSubmit() : handleSubmitForApproval())}
              disabled={submitting || submitBlockedByScope || scopeChecking}
              title={submitBlockedByScope ? "The deal doesn't match the carrier's scope yet." : scopeChecking ? "Checking the carrier's scope…" : undefined}
              className="px-4 py-2 text-sm font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? "Submitting…" : scopeChecking ? "Checking the scope…" : "Submit for Approval"}
            </button>
          )}
          {canRequestChanges && !showChangesForm && (
            <button
              onClick={() => setShowChangesForm(true)}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium bg-white border border-rose-300 text-rose-700 rounded-lg hover:bg-rose-50 disabled:opacity-50"
            >
              Request Changes
            </button>
          )}
          {canApprove && (
            <button
              onClick={() => handleApprove()}
              disabled={submitting || scopeBusy || (ncGateActive && !ncBalanced) || approveBlockedByScope || scopeChecking}
              title={
                approveBlockedByScope ? "The deal doesn't match the carrier's scope. Use the carrier's numbers, override with a reason, or send it back."
                : ncGateActive && !ncBalanced ? "Record the Non-Collect overage split (RM + TL + ASM must total the overage) before approving."
                : scopeChecking ? "Checking the carrier's scope…"
                : undefined
              }
              className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {submitting ? "Approving…" : "Approve Agreement"}
            </button>
          )}
          {canRelease && (
            <button
              onClick={handleReleaseToRobert}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit build"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
